import {
    TABLE_RAYTEAMS_LICENSE,
    TABLE_RAYTEAMS_PRODUCT_PACKAGE,
    skCorpAllocation,
    skDealerAllocation,
    isReceivedAllocationSk,
} from '../lib/rayteamsTables';
import {
    MAX_QR_PER_REQUEST,
    resolveSelfUsableCeiling,
    computeDealerRevokable,
    computeDealerRemaining,
    deriveMgrIdFromReceivedSk,
    filterLicensesByStatus,
    generateQrToken,
    isUpdateFailure,
    isValidId,
    isValidLcuseSk,
    isValidValiduntil,
    lockEq,
    lockGte,
    normalizeUpdateResult,
    scopeOwnDealers,
} from '../lib/smartDentQuota';
import {
    compensateQuotaDecrement,
    fetchProduct,
    fetchProductPackageInfo,
    queryCorpAllocation,
    queryDealerAllocation,
    queryDealerAllocationByDealerId,
    queryDealerAllocations,
    queryIssuedQrsByDealer,
    queryIssuerRedeemedQrs,
    queryLcuseRecord,
    putAllocationAudit,
    queryAllocationAudits,
    buildAllocationAuditItem,
} from '../lib/smartDentQueries';
import { GetManagerGroupIdByCompanyId } from '../lib/managerGroupResolver';
// PR #498/#499 리뷰 근본원인 해소 — mg 해석/sub-entity 판정 SSoT (BUG-184 로직 통합).
import { resolveScopeContext } from '../lib/scopeContext';
// IMP-220: voucher template DDB 조회 레이어 분리 — action↔DDB 직접 호출 제거.
import { fetchDefaultVoucherTemplate } from '../lib/voucherTemplateRepository';
import { clampQrBox, isValidQrBox } from '../lib/voucherTemplate';
import { MEMO_MAX_LEN, getLicenseRole, extractDealerIdFromSkStrict } from '../lib/licenseHelper';
import { filterRecordsByOwnerId } from '../lib/idorGuards';
import { mapWithConcurrency, resolveLicenseOwnerId } from '../lib/issuanceScope';
// FEAT-050 S5a: N단계 서브트리 fan-out 조립 순수 헬퍼.
// FEAT-050 S5b: BUG-170 봉인 해제 — clist 스코프 인가로 대체.
import {
    buildNodesByCompanyId, buildParentOf, isAllocationActorInScope, isIssuanceActorInScope,
    isDirectChildOf,
} from '../lib/allocationContext';
import { ORG_HQ, ORG_SUBSIDIARY } from '../config/appConstants';
import { v4 as uuidv4 } from 'uuid';
import { DoDB } from '../lib/ddb';
// axios: BE API (AppConfig.API_SERVER_PATH) 호출용.
import axios from 'axios';
import { AppConfig, JWT } from '../config';
import { SmartDentConstant } from './constants/SmartDentConstant';
import { AlertAction } from './AlertAction';
import { Translate } from '../config/Translate';
// FEAT-056(#560): 제품 인자 — 진입부에서 한 번 해석해 하위 조회·scope 에 명시 전달한다.
import { resolveLicenseProduct } from '../lib/licenseProduct';
import { LICENSE_PRODUCTS } from './constants/LicenseProductConstant';
import { resolveActorFromAuth } from './authActorHelper';
import { selectLicenseProductState } from '../reducers/SmartDentReducer';

// FEAT-057(#574): packageId ↔ 제품 정합 검사(IssueQr·AllocateToDealer·RevokeFromDealer 공용).
//   - 제품을 명시 전달: packageId 가 product.packageIds 에 없으면 거부(packageIds 가 빈 제품은 항상 거부).
//   - 제품 생략(기본 경로): packageId 가 **다른 제품**의 packageIds 에 속할 때만 거부. 어느 제품에도
//     속하지 않는 packageId 는 종전대로 통과한다.
// 거부 시 warn 을 남기고 false 를 반환한다(CLAUDE.md 규칙 7).
const isPackageAllowedForProduct = ({ handler, getState, product, productArg, packageId }) => {
    let reason = null;
    if (productArg != null) {
        if (!product.packageIds.includes(packageId)) reason = 'packageId is not in product.packageIds';
    } else {
        const owner = LICENSE_PRODUCTS.find((p) => p.key !== product.key && p.packageIds.includes(packageId));
        if (owner) reason = `packageId belongs to another product (${owner.key})`;
    }
    if (!reason) return true;
    console.warn(`[guard] ${handler}: packageId does not belong to product`, {
        handler,
        callerCompanyId: getState?.()?.AuthReducer?.user?.companyId ?? null,
        target: { product: product.key, packageId },
        reason,
    });
    return false;
};

// ─── B group: read operations (DynamoDB read via callDynamoV2) ───────────────

// B1. List Package products accessible to the current corp/dealer.
// Iterates known SMARTDent package ids in parallel; callers see the union.
// [SMARTDent spec] — supporting read (no direct SPEC-RG-* mapping; feeds
// the Package metadata that SPEC-RG-01 / 05 displays).
const GetPackages = (productArg) => async (dispatch, getState) => {
    try {
        const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.GetPackages', getState });
        if (!product) return [];
        // FEAT-022: rayteams-product 의 product 메타와 rayteams-product-package 의 패키지 메타
        // (qrpath 등) 를 packageId 별로 동시 조회 후 머지. qrpath 가 없으면 raw token만 QR 에 인코딩.
        const productRecords = await Promise.all(product.packageIds.map(fetchProduct));
        const packageInfoRecords = await Promise.all(product.packageIds.map((id) => (
            fetchProductPackageInfo(id).catch((e) => {
                console.warn('[SmartDentAction.GetPackages] product-package info fetch failed', id, e?.message);
                return null;
            })
        )));
        const packages = productRecords
            .map((p, i) => {
                if (!p) return null;
                const pkgInfo = packageInfoRecords[i];
                // qrpath, appitems 등 product-package 측 메타를 product 객체에 머지.
                // 충돌 시 product-package 가 우선 (qrpath 같은 발급 메타가 그쪽 SSoT).
                return pkgInfo ? { ...p, ...pkgInfo } : p;
            })
            .filter(Boolean);
        dispatch({ type: SmartDentConstant.GET_PACKAGES, packages, productKey: product.key });
        return packages;
    } catch (error) {
        console.error('[SmartDentAction.GetPackages]', error);
        return [];
    }
};

// B2. Get a single Package product by _id.
// [SMARTDent spec] — supporting read (no direct SPEC-RG-* mapping).
// FEAT-022: GetPackages 와 동일 머지 정책 — rayteams-product + rayteams-product-package
// (qrpath 등) 두 테이블 결합. GetPackage 가 GetPackages 의 머지를 누락하면 selectedPackage
// 갱신 시 qrpath 가 사라져 QR full URL 인코딩이 실패하는 회귀 발생.
const GetPackage = (packageId, productArg) => async (dispatch, getState) => {
    try {
        const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.GetPackage', getState });
        if (!product) return null;
        if (!product.packageIds.includes(packageId)) {
            console.warn('[guard] SmartDentAction.GetPackage: packageId does not belong to product', {
                handler: 'SmartDentAction.GetPackage',
                callerCompanyId: getState?.()?.AuthReducer?.user?.companyId ?? null,
                target: { product: product.key, packageId },
                reason: 'packageId is not in product.packageIds',
            });
            return null;
        }
        const [productRecord, pkgInfo] = await Promise.all([
            fetchProduct(packageId),
            fetchProductPackageInfo(packageId).catch((e) => {
                console.warn('[SmartDentAction.GetPackage] product-package info fetch failed', packageId, e?.message);
                return null;
            }),
        ]);
        const merged = productRecord && pkgInfo
            ? { ...productRecord, ...pkgInfo }
            : (productRecord || pkgInfo);
        dispatch({ type: SmartDentConstant.GET_PACKAGE, selectedPackage: merged, productKey: product.key });
        return merged;
    } catch (error) {
        console.error('[SmartDentAction.GetPackage]', error);
        return null;
    }
};

// BUG-206 (#369): 원장 소비 발급 경로(corp-direct · pool-participant)의 직속 자식 스코프.
//
// 스코프 판정은 tier 규약 SSoT `scopeOwnDealers` 에 위임한다 — 최상위는 `allocatedBy` 부재
// 레거시 행도 직속 자식으로 포함하고(빠지면 Σt 과소 → 천장 과대 = over-issuance), 중간 법인은
// 부모 mg 를 공유해 부재 행이 형제일 수 있어 strict 다(IMP-420 계약).
// 사전 리뷰 Blocker: 여기서 tier 무관 strict 를 쓰면 최상위 레거시 행이 Σt 에서 빠진다.
//
// tier 는 원장 sk 형태로 도출한다 — 2-seg(`info:{mg}`)면 자기 mg 원장 = 최상위, 3-seg
// (`info:{부모mg}:{자기}`)면 상위에서 받은 레코드 = 중간 법인. 데이터 의미 기반이라
// GetManagerGroupIdByCompanyId 가 부모 mg 를 truthy 반환하는 mg-truthy 함정에서도 안전하다.
// (셀프딜링 행 제외는 resolveSelfUsableCeiling 이 Σt·u 보정에 동일 적용한다.)
//
// PR #543 리뷰 Minor: strict 는 **3-seg 양성 판정**(isReceivedAllocationSk)으로 도출한다.
// `!isCorpLedgerSk` 로 뒤집으면 sk 부재·손상(`info:`, `info::`)이 strict 로 떨어져 부재 행이 Σt 에서
// 빠지고 천장이 과대해진다(over-issuance 방향 fail-open) — 미지값은 보수적인 비-strict 로 남긴다.
const scopeLedgerChildren = (rows, ownerCompanyId, ledgerRecord) =>
    scopeOwnDealers(rows, ownerCompanyId, isReceivedAllocationSk(ledgerRecord?.sk));

// IssueQr 예산 해석 관측용 warn dedup — 발급 요청마다 호출되므로 (companyId, mg, 예산종류)별 1회만 남긴다.
const warnedLedgerBudgetWithParentKeys = new Set();
// 테스트 간 격리 — 모듈 레벨 Set 은 suite 를 넘어 살아남아 warn 단언이 서로를 가린다
// (`smartDentQuota._clearNegativeSelfConsumedWarnCacheForTest` 선례).
export const _clearLedgerBudgetWarnCacheForTest = () => warnedLedgerBudgetWithParentKeys.clear();

// B3. Get the corp-level allocation record (sk = "info:{corpId}") for a Package.
// [SMARTDent SPEC-RG-01] — 법인/딜러의 SMARTDent Standalone License 보유 수량
// 확인. Initial(t_initial/u_initial) / Renewal(t_renewal/u_renewal)별 총량·사용·잔여는
// IMP-427: 할당 상한·집행 게이트는 가용량 SSoT resolveSelfUsableCeiling(Self-Usable) 사용.
// (표시 Remaining 의 legacy 폴백은 BUG-191 전제 미확정으로 종전 유지 — BUG-206(#369) 범위.)
// BUG-211: options.fresh — 이 read 는 Allocation Summary 표시용(기본, dedupe 적용)과
// 낙관적 락 재시도 등 "직전 실패 직후 최신값이 필요한" 호출(fresh:true, dedupe 우회) 둘 다 쓰인다.
const GetCorpAllocation = (packageId, corpId, options = {}) => async (dispatch, getState) => {
    try {
        const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetCorpAllocation', getState });
        if (!product) return null;
        // BUG-093/184 — sk 키 결정용 managerGroupId/sub-entity 판정을 resolveScopeContext SSoT 로 통일.
        const { managerGroupId, isSubEntity, receivedRecord } = await resolveScopeContext(
            corpId, { ...(options.fresh ? { fresh: true } : {}), product },
        );
        let record;
        if (isSubEntity) {
            record = receivedRecord; // 중간법인(공유/부모 mg 또는 mg-null): 받은 레코드가 자기 예산
        } else {
            record = managerGroupId
                ? await queryCorpAllocation(packageId, managerGroupId, { dedupe: true, fresh: !!options.fresh, product })
                : null;
        }
        dispatch({ type: SmartDentConstant.GET_CORP_ALLOCATION, corpAllocation: record ?? null, productKey: product.key });
        return record ?? null;
    } catch (error) {
        console.error('[SmartDentAction.GetCorpAllocation]', error);
        return null;
    }
};

// B4. List all dealer allocation records under a corp (sk begins_with "info:{corpId}:").
// [SMARTDent SPEC-RG-01 (법인 측) / SPEC-RG-06 (법인 전용: 딜러별 사용 현황)]
// 법인이 산하 딜러들의 보유·사용 현황을 한눈에 보는 용도.
//
// FEAT-024 AC-9 — pagination 옵션. options.limit / options.exclusiveStartKey 미전달 시
// 전체 일괄 로드(기존 동작). 200건+ 시나리오에서 페이징을 위한 옵션 진입로.
// BUG-211: options.fresh — GetAllocationSubtree 의 root 반복이 부르는 queryDealerAllocations
// 와 이 thunk 가 같은 managerGroupId 로 완전히 동일한 응답을 각자 조회하던 중복을, 표시 경로는
// dedupe(3초 TTL)로 합치고 AC-8 낙관적 락 재시도처럼 최신값이 필요한 호출은 fresh:true 로
// 우회한다(AllocateDealerModal.js).
const GetDealerAllocations = (packageId, corpId, options = {}) => async (dispatch, getState) => {
    // 해석을 try 밖에 둬야 catch 의 dispatch 가 product.key 를 쓸 수 있다.
    // 목록 밖 제품은 다른 제품 슬롯을 건드리지 않도록 dispatch 없이 반환(warn 은 resolveLicenseProduct 가 남김).
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetDealerAllocations', getState });
    if (!product) return [];
    try {
        // mg 해석·sub-entity 판정은 resolveScopeContext SSoT (BUG-182/184/187 통합).
        // IMP-390: 호출자가 이미 조회해 둔 received 를 넘겨받으면 재조회 생략(중복 제거).
        const { managerGroupId, isSubEntity } = await resolveScopeContext(corpId, {
            receivedRecord: options.receivedRecord,
            ...(options.fresh ? { fresh: true } : {}),
            product,
        });
        let dealerAllocations = managerGroupId
            ? await queryDealerAllocations(packageId, managerGroupId, { ...options, dedupe: true, fresh: !!options.fresh, product })
            : [];
        if (isSubEntity) dealerAllocations = scopeOwnDealers(dealerAllocations, corpId, true);
        dispatch({
            type: SmartDentConstant.GET_DEALER_ALLOCATIONS,
            dealerAllocations,
            productKey: product.key,
        });
        // IMP-420(#354) 문제 1: thunk 판정(SSoT)을 store 로 내려 FE 자체 isSubEntity 판정
        // (smartDentQuota.resolveEffectiveCorpBudget)의 대체 근거로 소비하게 한다. companyId
        // 를 함께 실어 소비처가 다른 회사 컨텍스트 오염을 검증할 수 있게 한다.
        dispatch({
            type: SmartDentConstant.GET_SCOPE_CONTEXT,
            scopeContext: { companyId: corpId, managerGroupId, isSubEntity },
            productKey: product.key,
        });
        return dealerAllocations;
    } catch (error) {
        console.error('[SmartDentAction.GetDealerAllocations]', error);
        // PR #538 리뷰 Minor: 성공 경로만 GET_SCOPE_CONTEXT 를 dispatch 하면 조회 실패 시
        // 직전 판정이 store 에 스테일로 남는다. null 로 무효화해 소비처가 override 미채택
        // (자체 판정 폴백)하도록 한다.
        dispatch({ type: SmartDentConstant.GET_SCOPE_CONTEXT, scopeContext: null, productKey: product.key });
        return [];
    }
};

// B5. Get a single dealer allocation record (sk = "info:{corpId}:{dealerId}").
// [SMARTDent SPEC-RG-01] — 법인 관리자가 특정 딜러의 현황을 상세 조회할 때 사용.
// 딜러 본인 측은 상위 법인 ID 를 모르는 상태이므로 GetMyDealerAllocation 사용.
const GetDealerAllocation = (packageId, corpId, dealerId, productArg) => async (dispatch, getState) => {
    try {
        const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.GetDealerAllocation', getState });
        if (!product) return null;
        // mg 해석·sub-entity 판정은 resolveScopeContext SSoT (BUG-182/184/187 통합).
        const { managerGroupId, isSubEntity } = await resolveScopeContext(corpId, { product });
        let record = managerGroupId
            ? await queryDealerAllocation(packageId, managerGroupId, dealerId, product)
            : null;
        if (isSubEntity && record && record.allocatedBy !== corpId) {
            record = null;
        }
        dispatch({ type: SmartDentConstant.GET_DEALER_ALLOCATION, dealerAllocation: record, productKey: product.key });
        return record;
    } catch (error) {
        console.error('[SmartDentAction.GetDealerAllocation]', error);
        return null;
    }
};

// 딜러 본인이 자신의 할당 record 를 조회 — 상위 법인 companyId 를 몰라도 동작.
// dealerId 의 sk suffix 패턴으로 직접 검색. record.allocatedBy 에 실제 corpId 가 들어
// 있으므로 호출자(LicenseDealerTab)가 이를 추출하여 후속 호출 (GetCustomerLicenses,
// GetIssuedQrsByDealer, GetVoucherTemplate)의 corpId 로 사용한다.
//
// 배경: parentCompanyId(딜러가 속한 조직 트리상 부모) 와 allocatedBy(실제 할당한 법인)
// 가 동일하지 않은 케이스 존재 (예: 상위 법인이 손자/증손자 dealer 에게 직접 할당).
// parentCompanyId 기반 corpId 추론으로는 정확한 allocation 조회 불가.
// BUG-211 R3(리뷰 Code Quality Major): 형제 3개 thunk 는 options.fresh 우회구가 있는데
// 이 thunk 만 dedupe:true 하드코딩이라 최신값을 강제할 방법이 없었다 — 딜러 자기뷰 수동
// Refresh(LicenseDealerTab.js fetchDealerSelfAllocation)가 이 thunk 하나만 dispatch 하므로
// (원래 여기엔 없앨 중복이 없었다) 재클릭해도 캐시만 반환하는 회귀였다. options.fresh 추가.
const GetMyDealerAllocation = (dealerId, options = {}) => async (dispatch, getState) => {
    // PR #191 review (Security Suggestion) — actor 와 dealerId 일치 검증.
    // queryDealerAllocationByDealerId 는 sk suffix 매칭으로 임의 dealer record 반환 가능
    // → 호출부 인자 wiring 실수 시 즉시 IDOR. assertCallerOwnsCompanyId 패턴 동일 적용.
    const actorCompanyId = getState?.()?.AuthReducer?.user?.companyId ?? null;
    if (!actorCompanyId || actorCompanyId !== dealerId) {
        console.warn('[SmartDentAction.GetMyDealerAllocation] COMPANY_ID_MISMATCH');
        return null;
    }
    try {
        const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetMyDealerAllocation', getState });
        if (!product) return null;
        // BUG-211: 이 무필터 전체스캔이 resolveScopeContext(mg-null 분기)·QueryMyReceivedAllocation
        // 이 같은 dealerId 로 각자 다시 실행하던 것과 완전히 동일한 응답이었다(Network 실측).
        // dedupe(10초 TTL)로 같은 corpId/dealerId 값이면 자동으로 합쳐진다.
        const record = await queryDealerAllocationByDealerId(dealerId, { dedupe: true, fresh: !!options.fresh, product });
        // IMP-387 dual-write: legacy 슬롯(dealer 세션 깊은 컴포넌트 무회귀) + 전용 A 슬롯(오염 민감 소비처).
        // options.receivedSlotOnly: 현황 카드가 발급현황(Self) 탭의 dealerAllocation 칸을 덮지 않도록 건너뛴다.
        if (!options.receivedSlotOnly) {
            dispatch({ type: SmartDentConstant.GET_DEALER_ALLOCATION, dealerAllocation: record, productKey: product.key });
        }
        dispatch({ type: SmartDentConstant.GET_MY_RECEIVED_ALLOCATION, myReceivedAllocation: record, productKey: product.key });
        return record;
    } catch (error) {
        console.error('[SmartDentAction.GetMyDealerAllocation]', error);
        return null;
    }
};

// BUG-179: 모달 self-sufficient — SubsidiaryTab/DealerTab 어느 진입점이든 자기 "받은"
// 예산을 조회한다. 공유 슬롯(dealerAllocation)은 DealerTab 에서 선택된 자식 레코드로
// 쓰이므로 여기서는 dispatch 하지 않고 값만 반환한다(슬롯 무오염). actor==corpId IDOR 가드.
const QueryMyReceivedAllocation = (corpId, options = {}) => async (_dispatch, getState) => {
    const actorCompanyId = getState?.()?.AuthReducer?.user?.companyId ?? null;
    if (!actorCompanyId || actorCompanyId !== corpId) {
        console.warn('[SmartDentAction.QueryMyReceivedAllocation] COMPANY_ID_MISMATCH', { actor: actorCompanyId, corpId });
        return null;
    }
    try {
        const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.QueryMyReceivedAllocation', getState });
        if (!product) return null;
        // BUG-211: GetMyDealerAllocation 과 같은 dedupe 캐시 — 모달 오픈 시 이 조회가 방금
        // 새로고침된 탭의 동일 corpId 조회와 겹치던 중복 제거. options.fresh 로 write 직후
        // 모달 재오픈 시 pre-write 상한이 보이지 않도록 우회 가능(리뷰 반영).
        return await queryDealerAllocationByDealerId(corpId, { dedupe: true, fresh: !!options.fresh, product });
    } catch (error) {
        console.error('[SmartDentAction.QueryMyReceivedAllocation]', error);
        return null;
    }
};

// ─── E1 read: customer licenses (issuer-first via QR -> lcuse) ───────────────

const CUSTOMER_LICENSE_DEFAULT_LIMIT = 50;
const CUSTOMER_LICENSE_MAX_LIMIT = 200;

// [SMARTDent SPEC-RG-06] — 딜러/법인이 자신의 SMARTDent Standalone License
// 고객 목록과 사용 현황 확인. 고객별 라이선스 종류(Initial/Renewal,
// sk의 type 접두어로 구분) · 만료일 · 중지 여부를 함께 내려준다. 법인 측
// 딜러별 현황은 GetDealerAllocations 로 보완.
//
// E1. List customer licenses issued by the current corp (optionally narrowed
// to a dealer and/or company). Chases from issuer -> QR -> license, not the
// other way around.
//
//   1. Query rayteams-product-package for QR records on each SMARTDent
//      package, filtered server-side by managerGroupId plus optional
//      dealerId / companyId, and used > 0 (redeemed only).
//   2. Flatten each QR's licenses[] snapshot into (appid, lcsk) refs.
//   3. Fetch the live lcuse record per ref (parallel) for the current
//      `suspended` flag and any post-redeem expired adjustments; fall back
//      to the QR snapshot when the live fetch fails.
//   4. Apply status filter and client-side pagination. `hasMore` signals
//      whether filtered results exceeded `limit`; cursor-based pagination
//      is not yet wired (the fan-out is entirely client-side), so consumers
//      should re-query with a larger limit rather than trying to page.
// targetAction: GET_CUSTOMER_LICENSES | GET_CORP_CUSTOMER_LICENSES | GET_DEALER_CUSTOMER_LICENSES
//
// 첫 인자 corpId — 호출자는 본인 companyId 또는 dealer 의 allocator corpId 를 전달.
// QR record 의 managerGroupId 필드는 actual manager-group _id(UUID)를 보유하므로,
// 본 thunk 가 corpId → UUID resolve 후 filter 에 사용. (이전 historical 명명 정정)
const GetCustomerLicenses = (corpId, options = {}, targetAction = SmartDentConstant.GET_CUSTOMER_LICENSES) => async (dispatch, getState) => {
    const { dealerId, companyId, status = 'all', limit = CUSTOMER_LICENSE_DEFAULT_LIMIT, from, to } = options;
    try {
        const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetCustomerLicenses', getState });
        if (!product) return { items: [], hasMore: false, total: 0 };
        const { managerGroupId, isSubEntity } = await resolveScopeContext(corpId, { product });
        if (!managerGroupId) {
            dispatch({ type: targetAction, customerLicenses: [], productKey: product.key });
            return { items: [], hasMore: false, total: 0 };
        }
        // #498 Major2: sub-entity(중간법인)는 parentMg 형제 발급분(userid PII) 노출 방지 위해 companyId=corpId 강제.
        const scopedCompanyId = isSubEntity ? corpId : companyId;
        const redeemedQrs = [];
        for (const packageId of product.packageIds) {
            const list = await queryIssuerRedeemedQrs({
                packageId,
                managerGroupId,
                dealerId,
                companyId: scopedCompanyId,
                from,
                to,
            });
            redeemedQrs.push(...list);
        }

        const refs = [];
        for (const qr of redeemedQrs) {
            const licenses = Array.isArray(qr.licenses) ? qr.licenses : [];
            for (const lic of licenses) {
                if (!lic?.appid || !lic?.lcsk) continue;
                refs.push({
                    appid: lic.appid,
                    lcsk: lic.lcsk,
                    lckey: lic.lckey ?? null,
                    snapshotExpired: lic.expired ?? null,
                    issuedAt: qr.used ?? null,
                    issuedFromQr: qr.sk,
                    // `userid` on the QR row is stamped at redeem time by the
                    // Homepage redeem flow; treat as user-asserted, not as a
                    // verified identity.
                    userid: qr.userid ?? null,
                    dealerId: qr.dealerId ?? null,
                    companyId: qr.companyId ?? null,
                });
            }
        }

        const liveRecords = await Promise.all(
            refs.map((ref) => queryLcuseRecord(ref.appid, ref.lcsk)),
        );

        const merged = refs.map((ref, i) => {
            const live = liveRecords[i];
            return {
                _id: ref.appid,
                sk: ref.lcsk,
                userid: ref.userid,
                issuedAt: ref.issuedAt,
                expired: live?.expired ?? ref.snapshotExpired,
                suspended: live?.suspended === true,
                memo: live?.memo ?? null,
                dealerId: ref.dealerId,
                companyId: ref.companyId,
                issuedFromQr: ref.issuedFromQr,
            };
        });

        // IMP-177 AC-3: dealer 컨텍스트(options.dealerId) 조회 시 응답 dealerId 소유권 검증.
        //   GetIssuedQrsByDealer / GetDealerCustomerLicenses 와 대칭. dealerId 부재(corp view,
        //   companyId 기반 또는 전사 조회)는 필터 skip — 전체 drop 회귀 방지. owner 부재 레코드는
        //   filterRecordsByOwnerId 내부에서 fail-open 보존.
        const ownerVerified = dealerId
            ? filterRecordsByOwnerId({
                records: merged,
                expectedId: dealerId,
                getOwnerId: (r) => r?.dealerId,
                guard: 'smartdent.customer-licenses',
            })
            : merged;
        const filtered = filterLicensesByStatus(ownerVerified, status);
        const clampedLimit = Math.min(
            Math.max(limit, 1),
            CUSTOMER_LICENSE_MAX_LIMIT,
        );
        const items = filtered.slice(0, clampedLimit);
        const hasMore = filtered.length > clampedLimit;

        dispatch({
            type: targetAction,
            customerLicenses: items,
            productKey: product.key,
        });
        return { items, hasMore, total: filtered.length };
    } catch (error) {
        console.error('[SmartDentAction.GetCustomerLicenses]', error);
        return { items: [], hasMore: false, total: 0 };
    }
};

// ─── E1-B: GetDealerCustomerLicenses — voucher_license MongoDB API (FEAT-032) ──
// dealer view 전용. voucher_license 컬렉션에서 dealerId 기준 조회.
// 기존 GetCustomerLicenses(corp view, DDB QR chain)와 병존.
const GetDealerCustomerLicenses = (dealerId, options = {}, targetAction = SmartDentConstant.GET_DEALER_CUSTOMER_LICENSES) => async (dispatch, getState) => {
    const { status = 'all' } = options ?? {};
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetDealerCustomerLicenses', getState });
    // 목록 밖 제품은 다른 제품 슬롯을 건드리지 않도록 dispatch 없이 반환(warn 은 resolveLicenseProduct 가 남김).
    if (!product) return { items: [] };
    const params = { status, product: product.key };
    if (dealerId) params.dealerId = dealerId;
    try {
        const res = await axios.get(
            `${AppConfig.API_SERVER_PATH}/voucher-license`,
            { ...JWT(), params },
        );
        const items = res.data?.data?.items ?? [];
        // IMP-177 AC-3: 응답 측 IDOR 검증 — dealerId truthy 시만 필터(corp view skip).
        // dealerId=null(corp view, x-ray-clist 다수 dealer) 은 필터 skip — 전체 drop 회귀 방지.
        const verifiedItems = dealerId
            ? filterRecordsByOwnerId({
                records: items,
                expectedId: dealerId,
                getOwnerId: (r) => r?.dealerId,
                guard: 'smartdent.customer-licenses',
            })
            : items;
        dispatch({ type: targetAction, customerLicenses: verifiedItems, productKey: product.key });
        return { items: verifiedItems };
    } catch (error) {
        console.error('[SmartDentAction.GetDealerCustomerLicenses]', error?.message ?? error);
        dispatch({ type: targetAction, customerLicenses: [], productKey: product.key });
        return { items: [] };
    }
};

// ─── E1-C: GetVoucherMetaByUser — voucher_license read-only meta (FEAT-034) ──
// PR #243 (FE) 후속. RAYGuard 사용자 권한 (cList) 스코프 밖 dealer 가 발급한 voucher
// 가 PC 의 RAYTeams 계정에 등록된 케이스의 발급 조직명 (groupName + dealerId) 조회.
// BE PR #119 의 GET /voucher-license/meta?userId&groupId 호출.
//
// dispatchRedux: false 패턴 (BUG-112 회귀 학습) — 호출자 (PCActivationPanel) 가 local
// state 보관. Redux mutation 없음.
//
// 응답 매핑:
//   - 200 → { groupName, dealerId, dealerName }
//   - 404 / 네트워크 에러 / userId 또는 groupId 누락 → null (silent fallback)
//
// 명시적 필드 선택 — BE 가 향후 spread 확산으로 추가 필드 노출해도 FE 가 silently
// 흡수하지 않도록. 정확히 { groupName, dealerId, dealerName } 만 추출.
//
// IMP-423: dealerName (발급 dealer 회사 표시명) 추가. companyMap 은 cList 스코프라
// 외부 dealer 를 찾지 못하므로 BE 가 이름을 함께 반환. 구버전 BE 응답 (필드 부재) 은
// null 로 정규화 — 호출부가 기존처럼 companyMap → raw ID 순으로 fallback.
// PR #244 review S-1 (CQ/Arch Suggestion): inner thunk 시그니처 — dispatch 인자 명시
// (현재 본 thunk 는 Redux mutation 없음, 호출자 local state 보관 패턴이지만 다른 thunk
// 와 시그니처 일관성 유지를 위해 _dispatch 명시. 추후 service layer 분리 시 pure async
// function 으로 전환 가능).
const GetVoucherMetaByUser = (userId, groupId, options = {}) => async (_dispatch, getState) => {
    if (!userId || !groupId) return null;
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetVoucherMetaByUser', getState });
    if (!product) return null;
    try {
        const res = await axios.get(
            `${AppConfig.API_SERVER_PATH}/voucher-license/meta`,
            // IMP-189: 404 를 정상 신호(매칭 voucher 없음)로 처리하므로 전역 deploy-skew 토스트 opt-out.
            { ...JWT(), params: { userId, groupId, product: product.key }, skipGlobalNotFoundHandler: true },
        );
        // PR 사전 리뷰 (Minor 1): payload 가 object 임을 명시적으로 검증.
        // BE 가 향후 잘못된 응답 (string 등) 보낼 경우 silently null fallback.
        const payload = res.data?.data;
        if (!payload || typeof payload !== 'object') return null;
        return {
            groupName: payload.groupName ?? null,
            dealerId: payload.dealerId ?? null,
            dealerName: payload.dealerName ?? null,
        };
    } catch (error) {
        // 404 (매칭 voucher 없음) 또는 네트워크 에러 — silent null fallback.
        // Sentry 등 별도 로깅은 적용하지 않음 (사용자 안내용 보조 정보라 noise 차단).
        return null;
    }
};

// ─── F group: FEAT-022 additions ─────────────────────────────────────────────

// F1. [FEAT-022 SPEC-RG-05 support] List all QR records issued by a dealer.
// Iterates product.packageIds; merges results for the issuance history table.
// Caller groups records by batchId to display per-batch rows with reprint action.
// 게이트웨이가 LastEvaluatedKey 를 내부 소진해 전량을 반환하므로 여기서 커서를 다룰 필요가
// 없다 — Limit 은 결과 상한이 아니라 페이지 크기다(IMP-462, 2026-09-09 실측).
const GetIssuedQrsByDealer = (dealerId, options = {}) => async (dispatch, getState) => {
    const { from, to, selfView } = options;
    try {
        const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetIssuedQrsByDealer', getState });
        if (!product) return [];
        // IMP-453(#435) — 자기발급이 원장(2-seg) 예산을 채택하면 QR write 에 dealerId 가
        // 없다(IssueQr 참고). 자기(self) 조회 화면에서 그 발급분이 기존 `dealerId = :dealerId`
        // 필터에 구조적으로 배제되지 않도록, 원장 소유자(받은-레코드 없음)일 때만 원장분
        // 포함 옵션을 쿼리에 얹는다. 중간법인(받은-레코드 존재)은 원장이 자기 예산이 아니므로
        // 전달하지 않는다(무회귀). selfView 미전달이면 resolveScopeContext 자체를 호출하지 않는다.
        // F-002(#561 PR 리뷰, BUG-208 후속) — mg 공유 토폴로지에서 receivedRecord 없는 두 회사가
        // 각각 원장 자기발급하면 dealerId 부재만으로는 주체를 구분할 수 없어(CWE-200), 원장분
        // 포함 절은 이제 managerGroupId 뿐 아니라 **companyId(자기)** 도 함께 요구한다
        // (`queryIssuedQrsByDealer` — dealerId 부재 AND managerGroupId 일치 AND companyId 일치).
        // IssueQr 이 원장 채택 시 QR 에 companyId=effectiveCorpId 를 강제 기록하도록 갱신됐으므로
        // (F-002) 이 이중 제약이 성립한다.
        let ledgerOptions = {};
        if (selfView) {
            const scope = await resolveScopeContext(dealerId, { product });
            if (scope?.managerGroupId && !scope.receivedRecord) {
                ledgerOptions = { includeLedgerIssued: true, managerGroupId: scope.managerGroupId };
            }
        }
        const allQrs = [];
        for (const packageId of product.packageIds) {
            const qrs = await queryIssuedQrsByDealer(packageId, dealerId, { from, to, ...ledgerOptions });
            allQrs.push(...qrs);
        }
        // IMP-453: filterRecordsByOwnerId 는 owner(dealerId) 부재 행을 fail-open 으로 통과시킨다
        // (아래 getOwnerId 참고) — 원장분(dealerId 부재) 행은 이 기존 fail-open 경로로 그대로
        // 생존한다. F-004(#561 PR 리뷰) 정정 — 이 fail-open 이 안전한 근거는 managerGroupId
        // **단독**이 아니라 **managerGroupId + companyId 이중 제약**(위 `queryIssuedQrsByDealer`
        // F-002 갱신)이다. mg 공유 토폴로지(BUG-208)에서는 mg 만으로 배타 경계가 성립하지
        // 않아(동일 mg 를 공유하는 타사 원장 QR 도 통과) 헬퍼 시그니처 변경 없이 안전하다는
        // 서술이 성립하려면 companyId 절이 함께 있어야 한다.
        // IMP-177 AC-3: 응답 측 IDOR 검증 — dealerId 부재 시 필터 skip — 전체 drop 회귀 방지(GetDealerCustomerLicenses 와 대칭).
        const verifiedQrs = dealerId
            ? filterRecordsByOwnerId({
                records: allQrs,
                expectedId: dealerId,
                getOwnerId: (r) => r?.dealerId,
                guard: 'smartdent.issued-qrs',
            })
            : allQrs;
        dispatch({
            type: SmartDentConstant.GET_ISSUED_QRS_BY_DEALER,
            issuedQrs: verifiedQrs,
            productKey: product.key,
        });
        return verifiedQrs;
    } catch (error) {
        console.error('[SmartDentAction.GetIssuedQrsByDealer]', error);
        return [];
    }
};

// ─── IMP-388 W4: 발급 현황 탭 데이터 계층 (값만 반환) ─────────────────────────
const ISSUANCE_FETCH_CONCURRENCY = 4;

// dealerIds 중복 제거 + 호출자 스코프(HQ su 우회 + 자기/clist, isAllocationActorInScope SSoT) 밖 제거.
// 제거분은 console.warn(CLAUDE.md 규칙 7).
const scopeIssuanceDealerIds = (dealerIds, actor, handler) => {
    const unique = Array.from(new Set(Array.isArray(dealerIds) ? dealerIds.filter(Boolean) : []));
    return unique.filter((id) => {
        if (isAllocationActorInScope(actor, id)) return true;
        console.warn('[guard] issuance dealerId out of scope', {
            handler,
            callerCompanyId: actor?.companyId ?? null,
            targetDealerId: id,
            reason: 'dealerId not in caller scope (self or clist)',
        });
        return false;
    });
};

// 이 두 thunk 는 공유 store 슬롯(dealerAllocations/scopeContext/allocationNodes/corpCustomerLicenses 등)에
// dispatch 하지 않는다 — 다른 탭 상태 오염 방지.
const QueryIssuedQrsForDealers = (dealerIds, options = {}) => async (_dispatch, getState) => {
    const handler = 'SmartDentAction.QueryIssuedQrsForDealers';
    try {
        const { from, to } = options ?? {};
        const product = resolveLicenseProduct(options?.product, { handler, getState });
        if (!product) return { items: [], failedDealerIds: [] };
        const actor = getState?.()?.AuthReducer?.user ?? null;
        const scoped = scopeIssuanceDealerIds(dealerIds, actor, handler);
        const tasks = [];
        scoped.forEach((dealerId) => {
            product.packageIds.forEach((packageId) => tasks.push({ dealerId, packageId }));
        });
        const results = await mapWithConcurrency(tasks, ISSUANCE_FETCH_CONCURRENCY, async (t) => {
            const qrs = await queryIssuedQrsByDealer(t.packageId, t.dealerId, { from, to });
            return Array.isArray(qrs) ? qrs : [];
        });
        const items = [];
        const failed = new Set();
        results.forEach((r, i) => {
            if (r?.ok) items.push(...r.value);
            else failed.add(tasks[i].dealerId);
        });
        if (failed.size > 0) {
            console.warn('[SmartDentAction.QueryIssuedQrsForDealers] partial failure', { failedDealerIds: Array.from(failed) });
        }
        return { items, failedDealerIds: Array.from(failed) };
    } catch (error) {
        console.error('[SmartDentAction.QueryIssuedQrsForDealers]', error?.message ?? error);
        return { items: [], failedDealerIds: [] };
    }
};

const QuerySubOrgCustomerLicenses = (dealerIds, options = {}) => async (_dispatch, getState) => {
    const handler = 'SmartDentAction.QuerySubOrgCustomerLicenses';
    try {
        const { status = 'all' } = options ?? {};
        const product = resolveLicenseProduct(options?.product, { handler, getState });
        if (!product) return { items: [], failedDealerIds: [] };
        const actor = getState?.()?.AuthReducer?.user ?? null;
        const scoped = scopeIssuanceDealerIds(dealerIds, actor, handler);
        const results = await mapWithConcurrency(scoped, ISSUANCE_FETCH_CONCURRENCY, async (dealerId) => {
            const res = await axios.get(
                `${AppConfig.API_SERVER_PATH}/voucher-license`,
                { ...JWT(), params: { status, dealerId, product: product.key } },
            );
            const rows = res?.data?.data?.items;
            // 응답 측 IDOR 검증 — 귀속 조직이 요청 dealerId 와 다른 행은 버린다.
            const list = Array.isArray(rows) ? rows : [];
            const kept = list.filter((r) => resolveLicenseOwnerId(r) === dealerId);
            if (kept.length < list.length) {
                console.warn('[guard] sub-org customer license rows dropped', {
                    handler,
                    callerCompanyId: actor?.companyId ?? null,
                    targetDealerId: dealerId,
                    droppedCount: list.length - kept.length,
                    reason: 'row owner does not match requested dealerId',
                });
            }
            return kept;
        });
        const items = [];
        const failedDealerIds = [];
        results.forEach((r, i) => {
            if (r?.ok) items.push(...r.value);
            else failedDealerIds.push(scoped[i]);
        });
        if (failedDealerIds.length > 0) {
            console.warn('[SmartDentAction.QuerySubOrgCustomerLicenses] partial failure', { failedDealerIds });
        }
        return { items, failedDealerIds };
    } catch (error) {
        console.error('[SmartDentAction.QuerySubOrgCustomerLicenses]', error?.message ?? error);
        return { items: [], failedDealerIds: [] };
    }
};

// ─── E2/E3 write: suspend / resume ──────────────────────────────────────────

// [SMARTDent SPEC-RG-07] — 딜러/법인이 고객 라이선스를 사용 중지 처리.
// FEAT-032: voucher_license(MongoDB) SSoT + rayteams-license(DDB) 양쪽 업데이트.
// _id = MongoDB ObjectId string. sk 파라미터 제거 (BE 가 sk 자동 조회).
//
// E2. Suspend a customer license by MongoDB _id.
const isMongoId = (id) => typeof id === 'string' && /^[a-f\d]{24}$/i.test(id);

// BUG-109 PR #227 Round 1 review Arch Major #19: BE SuspendLicense/ResumeLicense 응답
// 스키마 — 호출자가 record.sk 로 DDB sync thunk 를 dispatch 하므로 contract 명시.
//
// @typedef {Object} SuspendResumeRecord
// @property {string} _id      MongoDB ObjectId (24-hex)
// @property {string} sk       voucher_license sk — rayteams-license DDB voucher 레코드와 동일 값
//                              (PCActivationPanel.js:115 invariant: 'lcuse:region:groupId:userId:type:guid')
// @property {string} status   'active' | 'suspend' (BE PR #113 이후)
// @property {string} [suspendedBy]
// @property {number} [suspendedAt]
// @property {string} [suspendReason]
// @property {boolean} [ddbSyncPending]  BE PR #113: MongoDB 갱신 성공 + DDB sync FE 위임 신호
//
// @typedef {Object} SuspendResumeResult
// @property {boolean} ok
// @property {SuspendResumeRecord} [record]   ok=true 시 항상 존재 (BE 보장)
// @property {string} [reason]                ok=false 시: INVALID_IDENTIFIER, EMPTY_RESPONSE,
//                                              SUSPEND_CONFLICT_OR_MISSING, LICENSE_NOT_FOUND,
//                                              FORBIDDEN, UNAUTHENTICATED, NETWORK_ERROR, GATEWAY_ERROR
const SuspendLicense = ({ _id, reason, product: productArg }) => async (dispatch, getState) => {
    if (!isMongoId(_id)) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.SuspendLicense', getState });
    if (!product) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    try {
        const res = await axios.post(
            `${AppConfig.API_SERVER_PATH}/voucher-license/${encodeURIComponent(_id)}/suspend`,
            { reason: reason ?? '' },
            // IMP-189: 404(LICENSE_NOT_FOUND)를 정상 신호로 처리 → 전역 deploy-skew 토스트 opt-out.
            // FEAT-057: product 는 body 가 아니라 query(계약 voucher-license-api.yaml).
            { ...JWT(), params: { product: product.key }, skipGlobalNotFoundHandler: true },
        );
        const record = res.data?.data?.record ?? null;
        if (!record) return { ok: false, reason: 'EMPTY_RESPONSE' };
        dispatch({ type: SmartDentConstant.UPDATE_CUSTOMER_LICENSE, license: { ...record, source: 'voucher_license' }, productKey: product.key });
        dispatch(AlertAction.Success(Translate('common.message.success.saved')));
        return { ok: true, record };
    } catch (error) {
        console.error('[SmartDentAction.SuspendLicense]', error?.message ?? error);
        const status = error?.response?.status;
        if (status === 409) return { ok: false, reason: 'SUSPEND_CONFLICT_OR_MISSING' };
        if (status === 404) return { ok: false, reason: 'LICENSE_NOT_FOUND' };
        if (status === 403) return { ok: false, reason: 'FORBIDDEN' };
        if (status === 401) return { ok: false, reason: 'UNAUTHENTICATED' };
        if (!error?.response) return { ok: false, reason: 'NETWORK_ERROR' };
        return { ok: false, reason: 'GATEWAY_ERROR' };
    }
};

// [SMARTDent SPEC-RG-07] — 딜러/법인이 비활성화된 고객 라이선스를 재개.
// FEAT-032: voucher_license(MongoDB) SSoT + rayteams-license(DDB) 양쪽 업데이트.
// _id = MongoDB ObjectId string. sk 파라미터 제거.
//
// E3. Resume a customer license by MongoDB _id.
const ResumeLicense = ({ _id, reason, product: productArg }) => async (dispatch, getState) => {
    if (!isMongoId(_id)) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.ResumeLicense', getState });
    if (!product) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    try {
        const res = await axios.post(
            `${AppConfig.API_SERVER_PATH}/voucher-license/${encodeURIComponent(_id)}/resume`,
            { reason: reason ?? '' },
            // IMP-189: 404(LICENSE_NOT_FOUND)를 정상 신호로 처리 → 전역 deploy-skew 토스트 opt-out.
            { ...JWT(), params: { product: product.key }, skipGlobalNotFoundHandler: true },
        );
        const record = res.data?.data?.record ?? null;
        if (!record) return { ok: false, reason: 'EMPTY_RESPONSE' };
        dispatch({ type: SmartDentConstant.UPDATE_CUSTOMER_LICENSE, license: { ...record, source: 'voucher_license' }, productKey: product.key });
        dispatch(AlertAction.Success(Translate('common.message.success.saved')));
        return { ok: true, record };
    } catch (error) {
        console.error('[SmartDentAction.ResumeLicense]', error?.message ?? error);
        const status = error?.response?.status;
        if (status === 409) return { ok: false, reason: 'RESUME_CONFLICT_OR_MISSING' };
        if (status === 404) return { ok: false, reason: 'LICENSE_NOT_FOUND' };
        if (status === 403) return { ok: false, reason: 'FORBIDDEN' };
        if (status === 401) return { ok: false, reason: 'UNAUTHENTICATED' };
        if (!error?.response) return { ok: false, reason: 'NETWORK_ERROR' };
        return { ok: false, reason: 'GATEWAY_ERROR' };
    }
};

// E4. Update memo for a customer license.
//
// SEC-004 Phase 1 (2026-05-12) — BE 마이그레이션 + IDOR 가드 본질화.
// 종전: DoDB('update', ...) 로 rayteams-license DDB 직접 호출. BE IDOR 검증 부재.
// 신규: POST `${API_SERVER_PATH}/voucher-license/{_id}/memo` — BE 가 MongoDB voucher_license
// SSoT update + IDOR 가드 (user.su 또는 dealerId in x-ray-clist) + memo 길이 검증.
//
// 사용자 결정 (2026-05-12): memo 는 MongoDB SSoT 단일. DDB rayteams-license 측 memo
// 동기화는 책임 외 (Suspend/Resume 의 DDB sync 책임은 별개로 유지). 따라서 본 thunk 는
// BE 호출 + dispatch 만 수행. DDB 직접 호출 / sync 콜백 / 자동 retry 등 reconciliation
// 인프라 제거.
//
// 입력 검증·FE 캐시 매칭 가드는 defense-in-depth 로 유지 (BE 가드와 중복되지만
// 콘솔 직접 호출 시도를 빠르게 거절하여 BE 비용 절감 + 가시성 ↑).
//
// PR #230 review (Arch Minor / CQ Minor) — Suspend/Resume 와 동일하게 응답 contract
// typedef 명시. drift 방지.
//
// @typedef {Object} MemoUpdateRecord
// @property {string} _id              MongoDB ObjectId (24-hex)
// @property {string} sk               voucher_license sk — 'lcuse:{region}:{groupId}:{userId}:{type}:{guid}'
// @property {string} memo             갱신된 memo 값 (빈 문자열 가능)
// @property {string} [memoUpdatedBy]  BE 가 user.adminid 로 기록
// @property {number} [memoUpdatedAt]  BE 가 Date.now() 로 기록
//
// @typedef {Object} MemoUpdateResult
// @property {boolean} ok
// @property {(MemoUpdateRecord & Object)} [record]  ok=true 시 currentRecord overlay + source 마커
// @property {string} [reason]                       ok=false 시 reason enum:
//                                                    INVALID_IDENTIFIER, INVALID_SK, INVALID_MEMO,
//                                                    MEMO_TOO_LONG, UNAUTHENTICATED,
//                                                    LICENSE_NOT_VISIBLE, EMPTY_RESPONSE,
//                                                    INVALID_RESPONSE, MEMO_CONFLICT,
//                                                    LICENSE_NOT_FOUND, FORBIDDEN,
//                                                    NETWORK_ERROR, GATEWAY_ERROR
//
// currentRecord 파라미터: BE 응답 record 에 누락된 QR 유래 필드 (issuedAt/userid/dealerId)
// 를 보존하기 위해 호출자가 현재 license 객체 전체를 전달. overlay 순서는
// `{ ...currentRecord, ...record, source }` — BE 갱신 필드가 우선.
const UpdateLicenseMemo = ({ _id, sk, memo, currentRecord, product: productArg }) => async (dispatch, getState) => {
    // BE 엔드포인트가 ObjectId.isValid 강제 → FE 도 동일 형식 검증 (SuspendLicense 패턴).
    if (!isMongoId(_id)) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.UpdateLicenseMemo', getState });
    if (!product) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    if (!isValidLcuseSk(sk)) return { ok: false, reason: 'INVALID_SK' };
    if (typeof memo !== 'string') return { ok: false, reason: 'INVALID_MEMO' };
    if (memo.length > MEMO_MAX_LEN) return { ok: false, reason: 'MEMO_TOO_LONG' };

    const actor = resolveActorFromAuth(getState);
    if (!actor) return { ok: false, reason: 'UNAUTHENTICATED' };

    // FE 가시 라이선스 캐시 매칭 — 표/모달에서 진입한 라이선스만 편집 허용. 캐시는
    // GetCustomerLicenses / GetCustomerLicensesByDealer 결과로 IDOR 가드 통과한 데이터.
    const reducer = selectLicenseProductState(getState?.(), product.key) ?? {};
    const corpLics = Array.isArray(reducer.corpCustomerLicenses) ? reducer.corpCustomerLicenses : [];
    const dealerLics = Array.isArray(reducer.dealerCustomerLicenses) ? reducer.dealerCustomerLicenses : [];
    const visible = corpLics.concat(dealerLics)
        .some((lic) => lic && lic._id === _id && lic.sk === sk);
    if (!visible) {
        console.warn('[SmartDentAction.UpdateLicenseMemo] LICENSE_NOT_VISIBLE');
        return { ok: false, reason: 'LICENSE_NOT_VISIBLE' };
    }

    let record;
    try {
        const res = await axios.post(
            `${AppConfig.API_SERVER_PATH}/voucher-license/${encodeURIComponent(_id)}/memo`,
            { memo },
            // IMP-189: 404(LICENSE_NOT_FOUND)를 정상 신호로 처리 → 전역 deploy-skew 토스트 opt-out.
            { ...JWT(), params: { product: product.key }, skipGlobalNotFoundHandler: true },
        );
        record = res.data?.data?.record ?? null;
        if (!record) return { ok: false, reason: 'EMPTY_RESPONSE' };
        // PR #230 review (Sec Suggestion CWE-20): BE 응답 record 의 identity 검증.
        // _id 또는 sk 불일치 시 BE/gateway 손상 또는 응답 corruption — 절대 dispatch 금지.
        if (record._id !== _id || record.sk !== sk) {
            console.error('[SmartDentAction.UpdateLicenseMemo] record identity mismatch');
            return { ok: false, reason: 'INVALID_RESPONSE' };
        }
    } catch (error) {
        // PR #230 review (Sec Minor CWE-532): error 객체 직접 노출 대신 message 만 추출.
        console.error('[SmartDentAction.UpdateLicenseMemo]', error?.message ?? 'unknown error');
        const status = error?.response?.status;
        // PR #230 review (Arch Minor #10): 409 매핑 추가 — Suspend/Resume 패턴 일관성.
        if (status === 409) return { ok: false, reason: 'MEMO_CONFLICT' };
        if (status === 404) return { ok: false, reason: 'LICENSE_NOT_FOUND' };
        if (status === 403) return { ok: false, reason: 'FORBIDDEN' };
        if (status === 401) return { ok: false, reason: 'UNAUTHENTICATED' };
        if (!error?.response) return { ok: false, reason: 'NETWORK_ERROR' };
        return { ok: false, reason: 'GATEWAY_ERROR' };
    }

    // PR #230 review (CQ Major #2): dispatch payload 에 source:'voucher_license' 마커 추가.
    // SmartDentReducer.UPDATE_CUSTOMER_LICENSE 가 MongoDB _id 기준 매칭 분기를 타도록 함
    // (Suspend/Resume 동일 패턴).
    //
    // currentRecord(FE 캐시) base + BE record overlay — issuedAt/userid/dealerId 등 QR 유래
    // 필드 보존. source 는 가장 마지막 spread 로 항상 'voucher_license' 강제.
    const license = currentRecord
        ? { ...currentRecord, ...record, source: 'voucher_license' }
        : { ...record, source: 'voucher_license' };
    dispatch({ type: SmartDentConstant.UPDATE_CUSTOMER_LICENSE, license, productKey: product.key });
    return { ok: true, record: license };
};

// ─── C group: allocation writes (corp -> dealer) ─────────────────────────────

// [SMARTDent SPEC-RG-02] — 법인이 하위 딜러에게 License 수량 할당.
// Initial(t_initial)/Renewal(t_renewal) 구분 할당. 딜러 RAYGuard 에 즉시 반영됨.
//
// C1. Allocate quota from the corp to a dealer. Creates the dealer record if
// absent or increments t_initial / t_renewal if present. The corp record itself
// is never touched — corp invariance is the load-bearing simplification
// that keeps every write in this flow a single-record update.
//
// Remaining quota formula: `corp.t_* - corp.u_* - sum(dealer.t_*)`. The
// dealer-sum component is a read-then-write snapshot; concurrent allocations
// can race. The optimistic-lock ConditionExpression on the dealer record's
// t_initial / t_renewal catches concurrent writes to the same dealer row.
//
// TODO (concurrency): two concurrent AllocateToDealer calls targeting
// different dealers under the same corp can both pass their per-row
// optimistic-lock check and jointly exceed `corp.t_* - corp.u_*`. The spec
// (license-api.md) acknowledges this snapshot race. A cross-row barrier
// (e.g., a `corpRev` attribute bumped on every C1/C2/corp-direct D1 write
// and asserted in the condition) is the planned mitigation once the
// gateway's supported operations are pinned down.
const AllocateToDealer = ({
    packageId,
    corpId,
    dealerId,
    tNew = 0,
    tRenew = 0,
    product: productArg,
    // PR #184 R1 FYI #2 — actor 는 getState 에서 강제 추출하므로 caller param 미허용.
    // SuspendLicense / ResumeLicense 와 동일 보호 수준으로 통일하여 audit 위변조 차단.
}) => async (dispatch, getState) => {
    const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.AllocateToDealer', getState });
    if (!product || !isValidId(packageId) || !isValidId(corpId) || !isValidId(dealerId)) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    // FEAT-057: 다른 제품(또는 명시 제품 밖) packageId 는 DDB 조회·쓰기 이전에 거부.
    if (!isPackageAllowedForProduct({ handler: 'SmartDentAction.AllocateToDealer', getState, product, productArg, packageId })) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    const actorObj = resolveActorFromAuth(getState);
    if (!actorObj) return { ok: false, reason: 'UNAUTHENTICATED' };
    // PR #210 review — corp 인가 검증: actor 본인 법인만 할당 가능.
    // self-dealing(dealerId === corpId)은 Subsidiary 역할만 허용.
    const actorAuthUser = getState?.()?.AuthReducer?.user ?? null;
    const actorCompanyId = actorAuthUser?.companyId ?? null;
    if (!actorCompanyId || actorCompanyId !== corpId) {
        return { ok: false, reason: 'UNAUTHORIZED' };
    }
    if (dealerId === corpId && getLicenseRole(actorAuthUser) !== ORG_SUBSIDIARY) {
        return { ok: false, reason: 'UNAUTHORIZED' };
    }
    // BUG-198 v2: FEAT-050 S5b "skip-level 허용(clist 서브트리)" 반전 — 순차 할당 모델은
    // 각 조직이 자기 직속 자식에게만 할당할 수 있다(조부모→손자 건너뛰기 금지).
    // self-dealing(dealerId === corpId)은 위에서 이미 처리되어 여기 도달 안 함.
    // HQ(su) 는 전사 bypass(companies 로드 여부 무관, BUG-173 대비 유지).
    if (dealerId !== corpId && getLicenseRole(actorAuthUser) !== ORG_HQ) {
        const companies = getState?.()?.CompanyReducer?.companies;
        // fail-closed: companies 미로드/대상 미발견/직속 아님 → 모두 차단.
        if (!isDirectChildOf(companies, dealerId, corpId)) {
            console.warn('[guard] allocation target out of scope', {
                handler: 'SmartDentAction.AllocateToDealer',
                callerCompanyId: actorCompanyId,
                targetCompanyId: dealerId,
                reason: 'target is not a direct child (skip-level allocation prohibited)',
            });
            return { ok: false, reason: 'TARGET_NOT_DIRECT_CHILD' };
        }
    }
    if (!Number.isInteger(tNew) || !Number.isInteger(tRenew)) {
        return { ok: false, reason: 'INVALID_QUANTITY' };
    }
    if (tNew < 0 || tRenew < 0 || (tNew === 0 && tRenew === 0)) {
        return { ok: false, reason: 'INVALID_QUANTITY' };
    }

    // Blocker(BUG-200 PR #535 리뷰) — §12.1-A "자기 대상 할당 폐지": dealerId===corpId 이면
    // isSubEntity(중간법인 여부) 와 무관하게 조건 없이 fail-closed 차단한다. 종전에는
    // `isSubEntity && dealerId === corpId` 로 중간법인일 때만 걸러 부모와 manager-group 을
    // 공유하는 중간법인(queryCorpAllocation(부모mg) 성공 → isSubEntity=false 오도출)이
    // 우회할 수 있었다 — 우회 시 자기 받은-예산 행에 ADD t_initial + allocatedBy 자기
    // 덮어써 예산 2배 + 부모 귀속 탈취가 발생한다. 최상위 법인의 자기할당도 (나) 모델에서는
    // IssueQr(발급) 몫이지 할당이 아니므로 동일하게 차단한다. corpRecord/isSubEntity 조회
    // 이전에 배치해 공유-mg 우회 여지 자체를 없애고 불필요한 조회도 생략한다.
    // silent-drop 금지(CLAUDE.md #7): warn 동반.
    if (dealerId === corpId) {
        console.warn('[guard] self-allocation blocked', {
            handler: 'SmartDentAction.AllocateToDealer',
            callerCompanyId: corpId,
            targetCompanyId: dealerId,
            reason: 'self-allocation abolished (§12.1-A) — self-consume via IssueQr instead',
        });
        return { ok: false, reason: 'SELF_ALLOCATION_MIDCORP' };
    }

    // IMP-420(#354) PR #538 리뷰 Blocker: 판정을 resolveScopeContext(SSoT)로 통일한다.
    // 종전 자체 추정("원장 있으면 최상위")은 부모와 mg 를 공유하는 중간법인에서 부모 원장을
    // 자기 예산으로 오인해, 최상위 Σd 직속-only 필터와 결합되면 부모 풀 전량 배분이 열렸다.
    // SSoT 는 자기 받은 몫(info:{mg}:{corpId}) exact 조회로 판정하므로 공유 mg 오인이 없다.
    // IMP-426 PR #539 리뷰 Major: 이 판정이 write 대상 파티션(sk)을 결정하므로 mg 재배정
    // 직후에도 캐시 우회 — { fresh: true }(resolveScopeContext → GetManagerGroupIdByCompanyId 로 전파).
    const scope = await resolveScopeContext(corpId, { fresh: true, product });
    let managerGroupId = scope.managerGroupId;
    let isSubEntity = scope.isSubEntity;
    let corpRecord = isSubEntity
        ? scope.receivedRecord
        : (managerGroupId ? await queryCorpAllocation(packageId, managerGroupId, { product }) : null);
    if (!corpRecord) {
        // 하이브리드/미탐 폴백(무회귀): 자기 mg 는 해석됐지만 그 안에 원장도 받은 몫도 없는 회사는
        // 부모 mg 아래 받은 레코드가 예산이다(resolveScopeContext 는 자기 mg 내부만 보므로 놓친다).
        // 받은 몫이 있으면 중간법인으로 취급 — 예산은 좁은 쪽(받은 몫), 게이트는 selfUsable.
        const received = await queryDealerAllocationByDealerId(corpId, { product });
        if (received) {
            corpRecord = received;
            isSubEntity = true;
            if (!managerGroupId) managerGroupId = deriveMgrIdFromReceivedSk(received.sk, corpId);
        }
    }
    if (!corpRecord || !managerGroupId) return { ok: false, reason: 'CORP_ALLOCATION_NOT_FOUND' };

    const dealerList = await queryDealerAllocations(packageId, managerGroupId, { product });
    // BUG-180: 중간 법인이면 자기가 배분한 것만 Σ(형제/부모 딜러 제외) — BUG-177 SSoT 헬퍼 재사용.
    const scopedForGate = scopeOwnDealers(dealerList, corpId, isSubEntity);
    // FEAT-050 (나): 중간 법인은 자기 리딤(u)도 차감한 selfUsable(t−u−Σ자식.t)이 진짜 미약정 가용량.
    // (표시 selfUsable 과 게이트 일치 — over-commit 방지, PRReviewer Major 1).
    // IMP-427: 최상위도 동일 SSoT 로 통일됐다(종전 issuable 유지 규칙 폐기).
    // (나) v0.5: 게이트는 순수 u 기반 selfUsable(t−u−Σd). i(발행수)는 무한 발행 참고치라 미사용.
    // 사용자 확정: 예산이 없으면 리딤 요청이 실패하므로(예산 인지 redeem) 미리딤 바우처(i−u)는
    // over-commit 를 못 일으킨다 → i 항 불요. (2R 리뷰 Major 1: min(i,u) 제안은 이 모델상 불필요.)
    // 외부 불변식 출처: 예산 인지 redeem(예산 없으면 리딤 요청 실패)은 이 저장소 코드가 아니라
    // 외부(RAYTeams Homepage/BE)가 강제하는 불변식이다. 미리딤(i−u) 바우처는 예산 소진 시
    // 최종 활성화 단계에서 거부된다.
    // IMP-427 (사용자 확정 2026-07-31): 최상위 분기의 `issuable`(t−i−Σd)을 폐기하고 tier 무관
    // 가용량 SSoT(resolveSelfUsableCeiling)로 통일한다. 종전에는 표시·팝업 상한이 selfUsable 인데
    // 이 게이트만 `i` 를 차감해 **팝업이 허용한 수량이 여기서 INSUFFICIENT_CORP_QUOTA 로 거절**됐다.
    // `i` 는 참고 값이며 가용량 계산에 쓰지 않는다.
    //
    // 주의(PR #540 리뷰): "i ≥ u 이므로 selfUsable ≥ issuable" 은 성립하지 않는다 — 2-seg 원장의
    // `i` 는 자기 발급(비-롤업)이고 `u` 는 자손 리딤까지 합친 롤업이라 스코프가 다르다. 실제 방향은
    // 레코드별로 갈린다(예: i=0·u=30 이면 구식 100 → 신식 70 으로 천장이 하강). 이 케이스에서
    // 구식 게이트는 이미 리딤한 30 을 무시해 over-commit(30+80 > t 100)을 허용했다.
    //
    // BUG-191/BUG-200(롤업 이중차감) 방어와 직속 자식 모집단 규칙은 SSoT 내부에 있다 —
    // 근거·경계는 `smartDentQuota.resolveSelfUsableCeiling` 주석 참조.
    // allRows = mg 전체 3-seg 목록(`queryDealerAllocations(packageId, managerGroupId)`).
    // 원장(2-seg)의 u 는 **mg 그룹 전체** 롤업이라 자기소모를 얻으려면 직속뿐 아니라 그룹 내 전
    // 3-seg 리딤을 빼야 한다(사용자 확정 + 실데이터 검증 — 헬퍼 주석 참조).
    const issuable = resolveSelfUsableCeiling(corpRecord, scopedForGate, corpId, { allRows: dealerList });
    if (issuable.newQty < tNew || issuable.renewQty < tRenew) {
        return { ok: false, reason: 'INSUFFICIENT_CORP_QUOTA', issuable };
    }

    const dealerSk = skDealerAllocation(managerGroupId, dealerId);
    const existing = dealerList.find((d) => d?.sk === dealerSk) ?? null;
    const now = Date.now();

    const values = {
        ':now': now,
        ':corp': corpId,
    };
    // ADD 절 — tNew/tRenew 가 0 일 경우 해당 attribute 는 omit (게이트웨이 일부 환경에서
    // ADD <attr> 0 이 conditional check 우회 또는 비정상 응답을 유발하는 회귀 보호).
    const addParts = [];
    if (tNew > 0) {
        addParts.push('t_initial :tNew');
        values[':tNew'] = tNew;
    }
    if (tRenew > 0) {
        addParts.push('t_renewal :tRenew');
        values[':tRenew'] = tRenew;
    }
    let conditionExpr;
    // ExpressionAttributeNames — `#sk` 는 신규 record(`attribute_not_exists(#sk)`) 분기에서만
    // 사용. existing 분기 포함 시 DDB ValidationException ("unused in expressions") 으로 거부됨.
    const names = {};

    let updateExpr;
    if (existing) {
        values[':expTNew'] = existing.t_initial ?? 0;
        values[':expTRenew'] = existing.t_renewal ?? 0;
        // BUG-165: DynamoDB 부재 속성 ≠ 값 0. 기대값이 0이면 attribute_not_exists 를
        // 허용해 초기화된 적 없는 레코드의 스퓨리어스 ConditionalCheckFailed 를 방지.
        conditionExpr =
            `${lockEq('t_initial', ':expTNew', values[':expTNew'])} AND ` +
            `${lockEq('t_renewal', ':expTRenew', values[':expTRenew'])}`;
        updateExpr = 'SET allocatedAt = :now, allocatedBy = :corp';
        if (addParts.length > 0) {
            updateExpr += ' ADD ' + addParts.join(', ');
        }
    } else {
        names['#sk'] = 'sk';
        conditionExpr = 'attribute_not_exists(#sk)';
        // Initialise both i_* and u_* on creation so later decrements against
        // them are well-defined. u_* is written by Homepage on redeem;
        // i_* is written by IssueQr on voucher issuance.
        updateExpr =
            'SET allocatedAt = :now, allocatedBy = :corp, ' +
            'i_initial = if_not_exists(i_initial, :zero), i_renewal = if_not_exists(i_renewal, :zero), ' +
            'u_initial = if_not_exists(u_initial, :zero), u_renewal = if_not_exists(u_renewal, :zero)';
        values[':zero'] = 0;
        // BUG-165: addParts 에 들어가지 않는(값 0인) t_* 속성은 ADD 로 생성되지 않아
        // 이후 existing 분기의 attribute_not_exists 완화가 무력화된다. tNew/tRenew===0
        // 인 속성만 SET 으로 명시 초기화(같은 속성 SET+ADD 동시 사용 금지 — path overlap).
        if (tNew === 0) {
            updateExpr += ', t_initial = :zero';
        }
        if (tRenew === 0) {
            updateExpr += ', t_renewal = :zero';
        }
        if (addParts.length > 0) {
            updateExpr += ' ADD ' + addParts.join(', ');
        }
    }

    try {
        const params = {
            TableName: TABLE_RAYTEAMS_LICENSE,
            Key: { _id: product.licenseAppId, sk: dealerSk },
            UpdateExpression: updateExpr,
            ConditionExpression: conditionExpr,
            ExpressionAttributeValues: values,
            ReturnValues: 'ALL_NEW',
        };
        // ExpressionAttributeNames 는 비어 있을 경우 omit (DDB 거부 회피)
        if (Object.keys(names).length > 0) {
            params.ExpressionAttributeNames = names;
        }
        const result = await DoDB('update', params);
        const updated = normalizeUpdateResult(result);
        if (!updated) return { ok: false, reason: 'ALLOCATION_UPDATE_CONFLICT' };

        dispatch({
            type: SmartDentConstant.UPDATE_DEALER_ALLOCATION,
            allocation: updated,
            productKey: product.key,
        });
        dispatch(AlertAction.Success(Translate('common.message.success.saved')));

        // FEAT-024 AC-7 — best-effort audit write. 본 update 가 이미 성공했으므로
        // audit 실패 시 본 작업을 롤백하지 않고 console.error 로만 노출한다.
        // (audit 누락은 reconciliation 으로 처리, 본 PR 범위 외)
        const auditItem = buildAllocationAuditItem({
            corpId,
            operation: 'allocate',
            target: dealerId,
            tNew,
            tRenew,
            actor: actorObj.id,
            product,
        });
        if (auditItem) {
            const ok = await putAllocationAudit(auditItem);
            if (ok) {
                dispatch({
                    type: SmartDentConstant.APPEND_ALLOCATION_AUDIT,
                    audit: auditItem,
                    productKey: product.key,
                });
            }
        }

        return { ok: true, record: updated };
    } catch (error) {
        console.error('[SmartDentAction.AllocateToDealer]', error);
        return { ok: false, reason: 'GATEWAY_ERROR' };
    }
};

// [SMARTDent SPEC-RG-02] — 법인이 딜러에게 할당한 License 수량을 회수.
// 회수 시 미사용분(t_* - u_*)만 회수 가능 (이미 바우처 발급된 수량은 회수 불가).
//
// C2. Revoke unused quota from a dealer back to the corp. Per SPEC-RG-02 only
// the dealer's unused portion (`t_* - u_*`) can be revoked.
const RevokeFromDealer = ({
    packageId,
    corpId,
    dealerId,
    tNew = 0,
    tRenew = 0,
    product: productArg,
    // PR #184 R1 FYI #2 — actor 는 getState 강제 추출 (위 AllocateToDealer 와 동일).
}) => async (dispatch, getState) => {
    const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.RevokeFromDealer', getState });
    if (!product || !isValidId(packageId) || !isValidId(corpId) || !isValidId(dealerId)) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    // FEAT-057: AllocateToDealer 와 동일한 packageId ↔ 제품 검사.
    if (!isPackageAllowedForProduct({ handler: 'SmartDentAction.RevokeFromDealer', getState, product, productArg, packageId })) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    const actorObj = resolveActorFromAuth(getState);
    if (!actorObj) return { ok: false, reason: 'UNAUTHENTICATED' };
    // PR #210 review — corp 인가 검증: actor 본인 법인만 회수 가능.
    const actorAuthUserRevoke = getState?.()?.AuthReducer?.user ?? null;
    const actorCompanyIdRevoke = actorAuthUserRevoke?.companyId ?? null;
    if (!actorCompanyIdRevoke || actorCompanyIdRevoke !== corpId) {
        return { ok: false, reason: 'UNAUTHORIZED' };
    }
    if (!Number.isInteger(tNew) || !Number.isInteger(tRenew)) {
        return { ok: false, reason: 'INVALID_QUANTITY' };
    }
    if (tNew < 0 || tRenew < 0 || (tNew === 0 && tRenew === 0)) {
        return { ok: false, reason: 'INVALID_QUANTITY' };
    }

    // Major(BUG-200 PR #535 리뷰) — AllocateToDealer 의 §12.1-A 자기할당 폐지 가드(:745~753)
    // 와 대칭: 자기 할당이 불가해진 이상 자기 회수(dealerId===corpId)도 tier 무관 fail-closed
    // 차단한다. 중간법인의 self 행(info:{부모mg}:{self})을 회수하면 자기 t 가 깎이고 부모
    // 원장 기준 Σ자식.t 가 줄어 부모 가용이 늘어나는, 되돌릴 수 없는 예산 이전이 된다
    // (자기 할당이 막혀 상쇄 재할당이 불가능). 최상위 법인도 §12.1-A 정책상 금지 대상.
    // managerGroupId 해석/레코드 조회 이전에 배치해 불필요한 조회를 생략하고 우회 여지를
    // 없앤다. silent-drop 금지(CLAUDE.md #7): warn 동반.
    if (dealerId === corpId) {
        console.warn('[guard] self-revoke blocked', {
            handler: 'SmartDentAction.RevokeFromDealer',
            callerCompanyId: corpId,
            targetCompanyId: dealerId,
            reason: 'self-revoke abolished (§12.1-A) — self-allocation counterpart no longer exists to undo it',
        });
        return { ok: false, reason: 'SELF_REVOKE_BLOCKED' };
    }

    // BUG-199(#355) 문제 1 — AllocateToDealer 의 직속-자식 가드(:720~739)와 대칭: 할당이
    // 직속 자식 한정인데 회수는 조부모→손자 건너뛰기가 그대로 가능했다. 최상위 법인의
    // Dealer 목록(scopeOwnDealers 무필터)에 손자 행이 노출돼, 그 행을 회수하면 실제
    // 배분 주체(직속 부모)의 몫이 조용히 줄고 조부모 가용량만 늘어나는 되돌릴 수 없는
    // 예산 이전이 된다. HQ(su) 는 전사 bypass 유지(Allocate 와 동일). mg/레코드 조회
    // 이전에 배치해 우회 여지를 없앤다. silent-drop 금지(CLAUDE.md #7): warn 동반.
    // (PRReviewer 지적) self-revoke 는 바로 위(:967)에서 이미 fail-closed 반환되어 여기
    // 도달 시점엔 dealerId!==corpId 가 항상 성립 — Allocate 와 달리 가드 순서가 반대라
    // 별도 조건이 불필요하다.
    if (getLicenseRole(actorAuthUserRevoke) !== ORG_HQ) {
        const companies = getState?.()?.CompanyReducer?.companies;
        if (!isDirectChildOf(companies, dealerId, corpId)) {
            console.warn('[guard] revoke target out of scope', {
                handler: 'SmartDentAction.RevokeFromDealer',
                callerCompanyId: actorCompanyIdRevoke,
                targetCompanyId: dealerId,
                reason: 'target is not a direct child (skip-level revoke prohibited)',
            });
            return { ok: false, reason: 'TARGET_NOT_DIRECT_CHILD' };
        }
    }

    // BUG-093 — sk 키 결정용 managerGroupId 를 1회만 resolve (Major #1).
    // BUG-180: 중간 법인은 자기 mg 미해석 → 받은 레코드 sk 에서 상위mg 도출(회수 대상
    // 자식 레코드는 info:{상위mg}:{child} 네임스페이스에 존재). Allocate/IssueQr 와 동일 SSoT.
    // IMP-426 PR #539 리뷰 Major: write 대상 파티션 결정 — 캐시 우회.
    let managerGroupId = await GetManagerGroupIdByCompanyId(corpId, { fresh: true });
    if (!managerGroupId) {
        const received = await queryDealerAllocationByDealerId(corpId, { product });
        managerGroupId = deriveMgrIdFromReceivedSk(received?.sk, corpId);
    }
    if (!managerGroupId) return { ok: false, reason: 'DEALER_ALLOCATION_NOT_FOUND' };

    const dealerRecord = await queryDealerAllocation(packageId, managerGroupId, dealerId, product);
    if (!dealerRecord) return { ok: false, reason: 'DEALER_ALLOCATION_NOT_FOUND' };

    // SPEC-RG-02 정정 — SMARTDent over-issuance 비즈니스 모델 (사용자 확정):
    //   t = 활성화 budget, i = 발급된 voucher (over-print 허용), u = redeem
    //   회수 가능량 = t - u (이미 redeem 된 라이선스는 회수 불가, voucher 발급량(i) 무관).
    //   기존 코드는 t - i 사용했으나 over-issuance(i>t) 상황에서 음수 노출 + 회수 불가 회귀.
    const revokable = computeDealerRevokable(dealerRecord);
    if (revokable.newQty < tNew || revokable.renewQty < tRenew) {
        return { ok: false, reason: 'INSUFFICIENT_DEALER_QUOTA', issuable: revokable };
    }

    const tNewCur = dealerRecord.t_initial ?? 0;
    const tRenewCur = dealerRecord.t_renewal ?? 0;
    const iNewCur = dealerRecord.i_initial ?? 0;
    const iRenewCur = dealerRecord.i_renewal ?? 0;

    try {
        const result = await DoDB('update', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            Key: { _id: product.licenseAppId, sk: skDealerAllocation(managerGroupId, dealerId) },
            UpdateExpression: 'ADD t_initial :negTNew, t_renewal :negTRenew',
            // Optimistic lock on t_* and i_* catches concurrent allocation /
            // issuance on the same dealer row. u_* is Homepage-owned and does
            // not participate in the revoke check, so it is not locked.
            // Defense-in-depth: explicit `t_* >= :tNew/tRenew` guards prevent
            // the counter from becoming negative even if the optimistic lock
            // pre-check were ever bypassed.
            // BUG-165: DynamoDB 부재 속성 ≠ 값 0. t_* 잠금과 하한 가드는
            // 기대값(비교 대상)이 0일 때 attribute_not_exists 를 허용.
            // i_* 는 create 분기가 항상 if_not_exists 로 초기화해 부재가 있을 수 없으므로
            // (BUG-165 클래스와 무관) 엄격 비교 유지.
            ConditionExpression:
                `${lockEq('t_initial', ':expTNew', tNewCur)} AND ${lockEq('t_renewal', ':expTRenew', tRenewCur)} AND ` +
                'i_initial = :expINew AND i_renewal = :expIRenew AND ' +
                `${lockGte('t_initial', ':tNew', tNew)} AND ${lockGte('t_renewal', ':tRenew', tRenew)}`,
            ExpressionAttributeValues: {
                ':negTNew': -tNew,
                ':negTRenew': -tRenew,
                ':tNew': tNew,
                ':tRenew': tRenew,
                ':expTNew': tNewCur,
                ':expTRenew': tRenewCur,
                ':expINew': iNewCur,
                ':expIRenew': iRenewCur,
            },
            ReturnValues: 'ALL_NEW',
        });
        const updated = normalizeUpdateResult(result);
        if (!updated) return { ok: false, reason: 'REVOKE_UPDATE_CONFLICT' };

        dispatch({
            type: SmartDentConstant.UPDATE_DEALER_ALLOCATION,
            allocation: updated,
            productKey: product.key,
        });
        dispatch(AlertAction.Success(Translate('common.message.success.saved')));

        // FEAT-024 AC-7 — best-effort audit write (revoke 부호 자동 음수 정규화).
        const auditItem = buildAllocationAuditItem({
            corpId,
            operation: 'revoke',
            target: dealerId,
            tNew,
            tRenew,
            actor: actorObj.id,
            product,
        });
        if (auditItem) {
            const ok = await putAllocationAudit(auditItem);
            if (ok) {
                dispatch({
                    type: SmartDentConstant.APPEND_ALLOCATION_AUDIT,
                    audit: auditItem,
                    productKey: product.key,
                });
            }
        }

        return { ok: true, record: updated };
    } catch (error) {
        console.error('[SmartDentAction.RevokeFromDealer]', error);
        return { ok: false, reason: 'GATEWAY_ERROR' };
    }
};

// ─── D group: QR issuance ───────────────────────────────────────────────────

// [SMARTDent SPEC-RG-05] — 딜러/법인이 고객 전달용 SMARTDent Standalone
// License 바우처를 생성. 각 바우처는 QR 토큰(22자 Base62 CSPRNG) + 텍스트
// 코드 + 유효기간을 포함한다. UI 에서 HQ/법인 템플릿 이미지 위에 오버레이
// 하여 PDF/프린트 출력.
//
// SPEC-RG-05 준수 포인트: 발급 시 "보유 수량"(UI 에서 보이는 잔여) 은 차감
// 되지 않는다. 수량 차감(`u_*` 증가) 은 End User 가 QR 로 redeem 하는 시점
// 에 RAYTeams Homepage 가 담당한다. 본 함수는 발급 카운터 `i_*` 만 올리고
// 수량 잔여 검증 역시 `t_* - i_*` 기반으로 한다.
//
// D1. Issue `count` QR codes for a Package. When `dealerId` is provided the
// quota is drawn from the dealer record; otherwise it is drawn from the corp
// record. BUG-206 (#369): 원장 소비 경로(corp-direct · pool-participant)의 천장은
// 가용량 SSoT resolveSelfUsableCeiling(t − 자기소모 − Σ직속자식.t) 로 통일 — 종전
// `t − i − Σd`(발행 수 기준)는 리딤을 반영하지 못했다.
//
// Flow: read allocation(s) -> client-side quota check (t_* - i_* >= count)
// -> atomic `ADD i_* :count` with optimistic lock on the current i_* value
// -> write N QR records into rayteams-product-package. On a per-QR failure
// the loop issues a compensating `ADD i_* :-remaining` and returns a
// QR_WRITE_PARTIAL envelope so the caller can surface the discrepancy.
//
// TODO (concurrency): the corp-direct path (no dealerId) uses a read-time
// `sum(dealer.t_*)` that is NOT part of the optimistic lock. A concurrent
// AllocateToDealer on the same corp can land between the read and the
// `ADD i_* :count` and drive `corp.t_* - corp.i_* - sum(dealer.t_*)`
// negative. Mitigation tracked on AllocateToDealer (corpRev barrier).
// PR #187 리뷰 Major #3 — 파라미터 명명 오류 정정. `corpId` 가 정식 키이고 historical
// `managerGroupId` 는 deprecated alias. 두 키 모두 전달되면 동등성 검증 후 corpId 우선.
const IssueQr = ({
    packageId,
    type,
    corpId,
    /** @deprecated `corpId` 사용. 다음 cycle 에서 제거 예정. */
    managerGroupId,
    dealerId = null,
    // SEC-055 리뷰: QR 레코드에 `dealerId` 와 함께 보존되는 귀속 축이며 조회 필터로 쓰인다
    // (`smartDentQueries.js` `companyId = :cid`). **현재 전달하는 호출부는 0건**이라(전 트리 확인)
    // 스코프 검증을 넣지 않았다 — 전달하는 호출부가 생기면 `dealerId` 와 같은 판정에 태울 것.
    // (같은 축 헬퍼가 이미 있다: `assertCallerOwnsCompanyId` — GetVoucherTemplate 에서 사용.)
    // F-002(#561 PR 리뷰) 보강 — 위 "0건"은 **caller 가 이 파라미터로 전달하는** 경로 기준이다.
    // 함수 내부에서 원장 채택(`omitDealerIdOnQr`) 시 검증된 예산 주체(effectiveCorpId)를
    // `companyId` 로 파생 기록하는 로직이 추가됐으나(F-002), 이는 caller 전달이 아니라
    // corp-id-scope + budget-owner-self 가드를 통과한 값의 내부 강제이므로 위 서술과 모순되지
    // 않는다.
    companyId = null,
    validuntil,
    count = 1,
    product: productArg,
}) => async (dispatch, getState) => {
    const product = resolveLicenseProduct(productArg, { handler: 'SmartDentAction.IssueQr', getState });
    // 두 alias 동시 전달 시 동등성 assert (회귀 방지).
    if (corpId != null && managerGroupId != null && corpId !== managerGroupId) {
        return { ok: false, reason: 'CORP_MANAGER_GROUP_ID_MISMATCH' };
    }
    const effectiveCorpId = corpId ?? managerGroupId;

    // type 값 — 신규 명명 'initial'/'renewal' 표준. 'new'/'renew' 는 legacy alias 로
    // 한 cycle 유지 (호출부 점진 이전, 다음 IMP 에서 제거).
    const normalizedType =
        type === 'initial' || type === 'new' ? 'initial' :
        type === 'renewal' || type === 'renew' ? 'renewal' :
        null;
    if (!normalizedType) {
        return { ok: false, reason: 'INVALID_TYPE' };
    }
    if (!product || !isValidId(packageId) || !isValidId(effectiveCorpId)) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    // FEAT-056/057: packageId ↔ 제품 정합 — 명시 제품은 엄격, 기본 경로는 다른 제품 소속일 때만 거부.
    if (!isPackageAllowedForProduct({ handler: 'SmartDentAction.IssueQr', getState, product, productArg, packageId })) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    if (dealerId != null && !isValidId(dealerId)) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    if (companyId != null && !isValidId(companyId)) {
        return { ok: false, reason: 'INVALID_IDENTIFIER' };
    }
    if (!isValidValiduntil(validuntil)) {
        return { ok: false, reason: 'INVALID_VALIDUNTIL' };
    }
    if (!Number.isInteger(count) || count < 1) {
        return { ok: false, reason: 'INVALID_COUNT' };
    }
    if (count > MAX_QR_PER_REQUEST) {
        return { ok: false, reason: 'COUNT_EXCEEDS_MAX', max: MAX_QR_PER_REQUEST };
    }

    const issuedAttr = normalizedType === 'initial' ? 'i_initial' : 'i_renewal';
    const isDealerIssuance = !!dealerId;

    // FEAT-050 S5d Blocker (#480 PR#480 리뷰) — actor 는 getState 에서 강제 추출
    // (AllocateToDealer/RevokeFromDealer 와 동일 보호 수준). 실제 소비되는 budget owner
    // 기준 스코프는 allocation 분기가 확정된 뒤(budgetOwnerCompanyId 결정 후) 검사한다.
    const actorObj = resolveActorFromAuth(getState);
    if (!actorObj) return { ok: false, reason: 'UNAUTHENTICATED' };
    const actorAuthUser = getState?.()?.AuthReducer?.user ?? null;

    // SEC-055 (#373): `dealerId` 소속 검증. 형제 핸들러(AllocateToDealer:748 / RevokeFromDealer:1007)는
    // 이미 조직 트리 fail-closed 가드를 갖는데 발급만 0건이었다. pool-participant 폴백(대상 딜러의
    // 할당 레코드 없음)은 **자기 예산**을 소비하고 QR 에는 전달된 dealerId 를 보존하므로
    // (FEAT-049 redeem 라우팅), 아래 `isIssuanceActorInScope`(budgetOwner=자기만 검사)를 그대로
    // 통과한다 → 자기 예산이 차감되면서 QR 이 무관한 회사로 귀속·라우팅될 수 있었다.
    //
    // 경계 = **자기 또는 직속 자식** (PR #546 리뷰 R1 로 서브트리에서 좁힘). 근거:
    //   · 손자 발급은 UI 도달 경로가 0이다 — `LicenseDealerTab.js:418`
    //     `canOperate={selectedDealerId === companyId}`, 딜러 자기-뷰(:454-462)도 `dealerId=자기`.
    //   · 발행 서버 강제가 없어(SEC-056 #374 — rayteams 게이트웨이 passthrough) FE 가 유일 게이트다.
    //     그 표면은 최소 권한으로 잡는다. 손자 발급 UI 가 생기면 그때 근거와 함께 넓힌다.
    //
    // R2 리뷰의 "자기-only 로 더 좁히라" 는 채택하지 않았다 — **직속 자식 limb 은 thunk 계약으로
    // 살아 있다**. pool-participant(대상 딜러의 할당 레코드 없음 → 상위 예산 소비, QR 은 그 딜러로
    // 귀속)가 바로 그 형태이고, BUG-206(PR #543 머지)이 이를 통과 경로로 확정해 픽스처로 고정했다
    // (`SmartDentActionIssueQrLedgerGate.bug206.test.js` 4c/4d — `corpId=MID, dealerId=NO_REC`,
    // `qrRecords[0].dealerId === NO_REC`). 자기-only 로 좁히면 그 계약이 깨진다(실측: 두 스위트
    // 11케이스 red). UI 미노출은 "지금 버튼이 없다" 일 뿐 계약 폐기가 아니다.
    //
    // 판정 소스 = **`CompanyReducer.companies`**(서버 적재), `actorAuthUser.clist` 아님.
    // clist 는 로그인 시 localStorage 에 저장돼 부팅 때 그대로 재수화되므로(`AuthAction.js:30` →
    // `AuthReducer.js:6`) 임의 companyId 추가·`org:'HQ'` 위조로 무력화된다. 형제 가드
    // (AllocateToDealer:749 / RevokeFromDealer:1017)가 companies + parentCompanyId 를 쓰는 것과
    // 같은 소스·같은 fail-closed(미로드/미발견 → 차단)로 맞춰 판정 이원화도 없앤다.
    // HQ(su)는 companies 로드 여부와 무관하게 전사 bypass(BUG-173 대비 — 형제 가드 동일).
    // R2 리뷰(Security, 잔존 축 — 형제 가드도 동일): 서버 소스로 바뀐 것은 **대상 트리 축**
    // (targetCompany.parentCompanyId) 뿐이다. actor 식별(`actorAuthUser.companyId`)과 HQ 판정
    // (`companyInfo.org`)은 여전히 이 세션 재수화 값에 앵커돼 있다 — 다음 재사용처는 이 경계를
    // 서버급 신뢰 수준으로 오인하지 말 것.
    //
    // companies 로드 보증 지점(R2 리뷰): 할당 경로는 모달이 `CompanyReducer.companies` 를 직접
    // connect(`AllocateDealerModal.js:357`)해 후보 목록 렌더 시점에 로드가 보장되지만, 발급 경로
    // (`VoucherPrintModal`/`DealerIssuePanel`)는 `companyMap` prop 만 받고 이 슬라이스를 읽지
    // 않는다. 현재 도달 경로는 `isSelf` 뿐이라 오차단이 없으나(자기 대상은 companies 무관),
    // **직속-자식 발급 UI 를 추가할 때는 그 진입점에서 `GetCompany()` 로드를 함께 보장해야 한다** —
    // 그러지 않으면 fail-closed 로 정상 발급이 막힌다. 부모 탭(`LicenseDealerTab`)은 이미 로드한다.
    //
    // 배치: **모든 레코드 조회보다 앞**이다. 뒤에 두면 `DEALER_ALLOCATION_NOT_FOUND` /
    // `CORP_ALLOCATION_NOT_FOUND` 가 먼저 반환돼 스코프 밖 actor 가 "그 조직에 할당 레코드가
    // 있는지" 를 알 수 있다(이슈 보강 §2 — 존재 여부 정보 노출). 사유도 기존 스코프 차단과 같은
    // `TARGET_OUT_OF_SCOPE` 로 수렴시켜 응답으로 구분되지 않게 한다.

    // R2 리뷰(Security Major, 잔존 확인) — 위 가드는 `dealerId` 축만 본다. `isDealerIssuance &&
    // dealerId !== effectiveCorpId` 조건 자체가 `corpId` 축을 전혀 검사하지 않아, 아래 세 형태가
    // 모두 이 가드를 건너뛰거나 통과한 채 `:1296 resolveScopeContext(effectiveCorpId)` 로 진입했다:
    //   P1  corpId = dealerId = VICTIM              (조건 거짓 → 가드 skip)
    //   P2  dealerId 미전달(corp-direct)             (isDealerIssuance=false → 가드 skip)
    //   P3  corpId = VICTIM, dealerId = 자기 자신    (isSelf 가 무조건 통과)
    // 세 형태 모두 write 는 `:1490` 예산-주체 가드가 막지만(budgetOwnerCompanyId ≠ actor), 그 앞의
    // 조회(mg 해석·원장/받은-레코드 존재)가 실행돼 VICTIM 조직에 SmartDent 라이선스 데이터가
    // 있는지가 반환 사유(`DEALER_ALLOCATION_NOT_FOUND`/`CORP_ALLOCATION_NOT_FOUND` vs
    // `TARGET_OUT_OF_SCOPE`)로 샜다(CWE-200, read-only).
    //
    // 판정: `effectiveCorpId` 는 **actor 자신이거나 companies 트리 상 actor 의 조상**이어야 한다.
    // 자기 자신만 허용하면(단순 동치 검사) 딜러 자기-뷰(`LicenseDealerTab.js:46`
    // `corpId = dealerAllocation.allocatedBy`)를 깬다 — 그 값은 서버가 응답한 "누가 이 딜러에게
    // 배분했는가" 이고, 직속 부모뿐 아니라 조상일 수도 있다(같은 파일 주석 — "손자 dealer 에게
    // 상위 법인이 직접 할당"). 조상 관계는 `parentCompanyId` 체인으로 확인한다(`allocationGraph.js
    // resolveEffectiveOwner` 와 같은 walk 패턴 — `visited` Set 으로 순환 방지). 이렇게 하면
    // corpId=자기(A/B)·corpId=진짜 조상(C) 은 통과하고 corpId=무관 조직(P1/P2/P3)은 companies
    // 미로드 포함 fail-closed 로 막힌다.
    //
    // 이 검사는 `isDealerIssuance` 조건과 무관하게 **항상** 실행한다 — P1/P2 가 바로 그 조건 자체를
    // 우회하는 형태였기 때문이다.
    if (getLicenseRole(actorAuthUser) !== ORG_HQ) {
        const actorCompanyId = actorAuthUser?.companyId ?? null;
        const parentOf = buildParentOf(getState?.()?.CompanyReducer?.companies);
        const isSelfOrAncestor = !!actorCompanyId && (effectiveCorpId === actorCompanyId || (() => {
            const visited = new Set();
            let cur = parentOf[actorCompanyId];
            while (cur && !visited.has(cur)) {
                if (cur === effectiveCorpId) return true;
                visited.add(cur);
                cur = parentOf[cur];
            }
            return false;
        })());
        if (!isSelfOrAncestor) {
            console.warn('[guard] issuance corpId out of scope', {
                guard: 'corp-id-scope',   // target-dealer-scope(대상)·budget-owner-self(예산주체) 와 축 구분
                handler: 'SmartDentAction.IssueQr',
                callerCompanyId: actorCompanyId,
                targetCompanyId: effectiveCorpId,
                reason: 'effectiveCorpId is neither self nor an ancestor of actor (companies-based; HQ bypass)',
            });
            return { ok: false, reason: 'TARGET_OUT_OF_SCOPE' };
        }
    }

    // 자기발급(dealerId === effectiveCorpId)은 대상이 자기이므로 대상에서 제외한다.
    if (isDealerIssuance && dealerId !== effectiveCorpId
        && getLicenseRole(actorAuthUser) !== ORG_HQ) {
        const actorCompanyId = actorAuthUser?.companyId ?? null;
        // R2 리뷰(Security) — actor 의 companyId 가 falsy 면 판정 전에 **차단**한다.
        // 없으면 `''` actor 가 `parentCompanyId: ''` 인 **모든 루트 회사**를 직속 자식으로 만들어
        // fail-open 이었다(`''` 는 이 저장소가 실데이터로 인정하는 루트 표기 —
        // `licenseHelper.js:163-165`; `null` 만 있는 행도 `actorCompanyId === null` 과 매칭).
        // `resolveActorFromAuth` 는 `adminid` 만 요구해 :1212 를 통과시키므로 여기서 닫아야 한다.
        // 형제 가드(AllocateToDealer:738 / RevokeFromDealer:979)의 `!actorCompanyId → UNAUTHORIZED`
        // 와 같은 축이며, 사유는 정보 노출 차단을 위해 기존 코드로 수렴시킨다.
        const companies = getState?.()?.CompanyReducer?.companies;
        const isSelf = !!actorCompanyId && dealerId === actorCompanyId;
        const isDirectChild = !!actorCompanyId && isDirectChildOf(companies, dealerId, actorCompanyId);
        if (!isSelf && !isDirectChild) {
            console.warn('[guard] issuance target dealer out of scope', {
                guard: 'target-dealer-scope',   // :1444 budget-owner-self 가드와 축 구분(리뷰 반영)
                handler: 'SmartDentAction.IssueQr',
                callerCompanyId: actorCompanyId,
                targetCompanyId: dealerId,
                companiesLoaded: Array.isArray(companies),
                reason: 'dealerId is neither self nor a direct child (companies-based; HQ bypass)',
            });
            return { ok: false, reason: 'TARGET_OUT_OF_SCOPE' };
        }
    }

    // BUG-093 — sk 키 결정용 managerGroupId. BUG-180: 중간 법인 mg 미해석 → 받은 레코드 sk 에서
    // 상위mg 도출. IMP-426 PR #539 리뷰 Major: write 대상 파티션 결정이라 캐시 우회({ fresh: true }).
    //
    // BUG-206 R2 (PR #543 리뷰 Blocker 2건 + Minor): 원장 소비 경로의 예산은 **received-first** 다.
    // 종전 두 분기는 `queryCorpAllocation(resolvedMgrId)` 원장 우선이었는데, 부모와 manager-group 을
    // 공유하는 중간 법인은 mg 해석이 부모 mg 를 truthy 반환하므로(mg-truthy 함정 — AllocateToDealer 는
    // PR #538 Blocker 로 이미 교정) **부모 원장**이 자기 예산으로 잡혔다. 그러면 tier 도 최상위로
    // 판정돼 부모 풀 전량이 열리고 `i` 증분도 부모 원장 sk 에 기록됐다.
    //
    // mg 해석 · sub-entity 판정 · 받은 레코드는 스코프 SSoT(`resolveScopeContext`)로 통일한다 —
    // AllocateToDealer:790 과 같은 판정 하나만 쓴다. 인라인 복제였을 때는 mg-null 경로에서 받은
    // 레코드를 두 번 읽고, `allocatedBy === 자기`(레거시 오염) 행에서 SSoT 와 판정이 반대였다
    // (SSoT 는 그 행을 예산으로 채택, 사본은 부모 원장으로 낙하 = 닫은 Blocker 와 같은 방향).
    const scope = await resolveScopeContext(effectiveCorpId, { fresh: true, product });
    const resolvedMgrId = scope.managerGroupId;
    if (!resolvedMgrId) {
        return { ok: false, reason: isDealerIssuance ? 'DEALER_ALLOCATION_NOT_FOUND' : 'CORP_ALLOCATION_NOT_FOUND' };
    }
    // 자기 예산(받은 레코드). null 이면 원장이 예산이다.
    const ownReceivedBudget = scope.receivedRecord ?? null;

    // BUG-206 R2 (PR #543 리뷰 Minor): received-first 판정은 `allocatedBy` 에 의존하므로 **레거시
    // 오염 데이터에서 무효화**된다 — PR #535 이전의 자기할당 우회는 자기 받은-예산 행의 allocatedBy 를
    // 자기로 덮어썼고(이 파일 :768-770), 그 행이 남은 중간 법인은 받은 레코드가 없는 것으로 판정돼
    // 부모 원장이 다시 예산·tier 가 된다(sk 는 정상 받은 레코드와 같은 키라 sk 로 구분 불가).
    //
    // 조직 트리로는 "부모가 있는데 받은 몫이 없다" 를 감지할 수 있으나, 자기 mg 를 보유한 하이브리드
    // 중간 법인(원장이 정말 자기 것)도 같은 형태이므로 **차단하면 정당한 발급을 막는다**. 따라서
    // 관측만 남기고(무음 금지 — CLAUDE.md #7) 보수 처리는 후속 이슈(SEC-057)로 분리한다.
    //
    // BUG-206 R3 (PR #543 리뷰 Minor): **원장을 실제 예산으로 채택한 지점에서만** 호출한다. 종전에는
    // `!ownReceivedBudget` 만 보고 함수 앞에서 판정해, 자기 딜러 레코드로 발급하는 경로(원장 미조회)와
    // 일반 딜러 발급에서도 오탐이 남았다 — 그 오탐이 dedup 키를 먼저 점유하면 같은 (회사, mg) 조합의
    // 진짜 원장 소비 케이스 warn 이 영구 억제된다.
    const warnLedgerBudgetWithParent = () => {
        const companies = getState?.()?.CompanyReducer?.companies;
        const self = Array.isArray(companies) ? companies.find((c) => c?.companyId === effectiveCorpId) : null;
        if (!self?.parentCompanyId) return;
        const dedupKey = `${effectiveCorpId}|${resolvedMgrId}|ledger`;
        if (warnedLedgerBudgetWithParentKeys.has(dedupKey)) return;
        warnedLedgerBudgetWithParentKeys.add(dedupKey);
        console.warn('[quota] IssueQr: 부모가 있는 법인이 원장을 예산으로 사용 — 받은 몫 미발견', {
            handler: 'SmartDentAction.IssueQr',
            callerCompanyId: effectiveCorpId,
            parentCompanyId: self.parentCompanyId,
            managerGroupId: resolvedMgrId,
            reason: '자기 mg 보유(하이브리드)면 정상, 레거시 allocatedBy 덮어쓰기면 부모 원장 오인 — 판정 근거 확인 필요',
        });
    };

    // 1. Locate allocation and verify quota client-side against i_*.
    // FEAT-050 S5d: 판별 기준 = 자기 레코드 존재 여부(잔여값 아님). 자기 레코드가 있으면
    // (설령 소진돼도) 그것만 소비하고 상위로 우회하지 않는다 — 발급 이력(i)이 노드
    // 간 섞이지 않도록 하기 위함. explicit dealer 는 redeem 기반 remaining(t−u), corp/owner
    // 는 selfUsable(t − 자기소모 − Σ직속자식.t) — 둘 다 리딤 기준이다(BUG-206 #369 로 통일).
    let allocSk;
    let currentIssued;
    let issuableByType;
    let budgetOwnerCompanyId;
    // IMP-453(#435) — 최상위 법인 자기발급이 원장(2-seg)을 예산으로 채택할 때만 QR write 에
    // dealerId 를 기록하지 않는다. RAYTeams HQ 통계에서 원장 발급분을 구분하기 위함이며,
    // 그 리딤은 원장 u 로 정산된다(rayteams 합의 2026-08-31). 중간법인(3-seg 받은-레코드
    // 예산)은 u 귀속을 위해 dealerId 를 유지해야 하므로 이 플래그는 원장 채택 두 지점에서만
    // true 가 된다. F-001(#561 PR 리뷰) 정정 — 생략은 no-silent-drop(CLAUDE.md #7) 예외가
    // 아니라, 생략 확정 시 발급 요청당 1회 warn 으로 관측한다(QR write 루프 직전).
    let omitDealerIdOnQr = false;
    if (isDealerIssuance) {
        const record = await queryDealerAllocation(packageId, resolvedMgrId, dealerId, product);
        if (dealerId === effectiveCorpId) {
            // BUG-200 후속(#361) — 자기발급(dealerId===effectiveCorpId) 게이트를 가용량
            // SSoT(resolveSelfUsableCeiling, BUG-206 R2 로 통일)로 단일화. write 대상
            // (allocSk/currentIssued)은 자기 레코드 유무 판정을 기존 그대로 유지하고,
            // 천장(issuableByType)만 selfUsable 로 교체한다. isSubEntity 판정은 mg 해석
            // 경로가 아니라 데이터 의미(record.allocatedBy!==effectiveCorpId — 받은-레코드
            // vs 자기 생성 self-deal)로 한다 — GetManagerGroupIdByCompanyId 가 부모 mg 를
            // truthy 반환하는 mg-truthy 함정에서도 안전.
            const isSubEntity = !!record && record.allocatedBy !== effectiveCorpId;
            let base;
            if (record) {
                // BUG-206 R3 (PR #543 리뷰 Blocker): 이 분기도 **received-first** 로 통일한다. 종전에는
                // `record.allocatedBy` 단독 판정이라, allocatedBy 가 자기로 덮어써진 레거시 행에서
                // isSubEntity=false → base = 부모 원장이 되어 다른 두 분기가 닫은 Blocker(부모 풀 전량
                // 개방 + 부모 원장 sk 에 i 증분)가 이 경로로 재현됐다. 스코프 SSoT 가 자기 예산을
                // 찾았으면 그것이 base 다.
                const budgetFromReceived = ownReceivedBudget ?? (isSubEntity ? record : null);
                base = budgetFromReceived ?? await queryCorpAllocation(packageId, resolvedMgrId, { product });
                // BUG-206 R2 (PR #543 리뷰 Minor): write 대상 = **예산 레코드**. 다른 두 원장 소비
                // 분기와 같은 규칙이다. 종전에는 예산이 원장(레거시 셀프딜 행 보유 최상위)인데도
                // `i` 낙관잠금을 셀프딜 행에 걸어, 같은 원장을 소비하는 corp-direct 분기와 **다른
                // 레코드에 락**을 잡았다 → 동시 발급 시 상호 배제가 성립하지 않아 원장 초과 발급이
                // 가능했다(BE 상한 부재 — SEC-056). 락 스코프를 예산 레코드로 일치시킨다.
                const budgetIsLedger = !!base && !budgetFromReceived;
                allocSk = budgetIsLedger ? skCorpAllocation(resolvedMgrId) : skDealerAllocation(resolvedMgrId, dealerId);
                currentIssued = (budgetIsLedger ? base[issuedAttr] : (budgetFromReceived ?? record)[issuedAttr]) ?? 0;
                if (budgetIsLedger) {
                    warnLedgerBudgetWithParent();
                    omitDealerIdOnQr = true; // IMP-453 — 원장 채택 지점 ①
                }
            } else {
                // 자기 레코드 없는 pool participant 최상위 법인 — owner(corp) 원장이 base.
                base = await queryCorpAllocation(packageId, resolvedMgrId, { product });
                if (!base) return { ok: false, reason: 'DEALER_ALLOCATION_NOT_FOUND' };
                // 원장 채택 4번째 지점 — 형제 3지점과 동일하게 관측(R4 리뷰 Minor).
                warnLedgerBudgetWithParent();
                omitDealerIdOnQr = true; // IMP-453 — 원장 채택 지점 ②(record 부재 pool-participant)
                allocSk = skCorpAllocation(resolvedMgrId);
                currentIssued = base[issuedAttr] ?? 0;
            }
            if (!base) {
                // corp 원장 부재(비정상 edge — 자기 레코드는 있으나 corp 원장 없음) — 좁은
                // 자기 레코드 몫(t-u)으로 안전 폴백(무회귀).
                const free = computeDealerRemaining(record);
                issuableByType = normalizedType === 'initial' ? free.newQty : free.renewQty;
            } else {
                const dealers = await queryDealerAllocations(packageId, resolvedMgrId, { product });
                // PR #538 리뷰 Major #1: GetAllocationSubtree(표시 SSoT, tier 무관 통일)와 달리
                // 여기만 `if (isSubEntity)` 조건이 남아 최상위 자기발급에서 필터가 전혀 적용되지
                // 않아 같은 노드의 selfUsable 이 표시와 발행 게이트에서 다른 값이 됐다. tier 무관
                // 통일 — 스코프 판정은 scopeLedgerChildren(scopeOwnDealers SSoT 위임)에 있다.
                //
                // BUG-206 R2 (PR #543 리뷰 Major): 원장 소비 **3번째 경로**. 종전에는 이 분기만 노드
                // 경로(buildNodesByCompanyId + computeNodeQuotaBreakdown)였고, 노드 조립은 2-seg 원장의
                // `u` 를 0 으로 폐기한다(자기소모는 셀프딜 행에만 있다는 가정). 셀프딜 행 없이 원장 `u`
                // 에만 자기 리딤이 있는 데이터에서는 천장이 다른 두 경로보다 커졌다 — IMP-425 로 셀프딜
                // 행 생성이 폐지된 뒤에는 그 형태가 정상이므로 원장 소비 3경로를 같은 SSoT 로 통일한다.
                const ceiling = resolveSelfUsableCeiling(
                    base,
                    scopeLedgerChildren(dealers, effectiveCorpId, base),
                    effectiveCorpId,
                    { allRows: dealers },
                );
                issuableByType = normalizedType === 'initial' ? ceiling.newQty : ceiling.renewQty;
            }
            budgetOwnerCompanyId = effectiveCorpId;
        } else if (record) {
            allocSk = skDealerAllocation(resolvedMgrId, dealerId);
            currentIssued = record[issuedAttr] ?? 0;
            // 인쇄 제한은 고객 활성화 소진 여부(remaining = t - u)로 판단.
            // 이미 인쇄된 수량(i_*)은 제한 기준이 아님 — 활성화 전 재출력 허용.
            const free = computeDealerRemaining(record);
            issuableByType = normalizedType === 'initial' ? free.newQty : free.renewQty;
            budgetOwnerCompanyId = dealerId;
        } else {
            // 자기 레코드 없는 pool participant만 owner(corp) 예산을 공유한다(자기 레코드
            // 보유 노드는 소진돼도 상위 미소비). QR 레코드에는 말단 dealerId 를 그대로
            // 유지(FEAT-049 redeem 라우팅 보존) — 이 블록은 quota 원천만 corp 로 바꾼다.
            // BUG-206 R2 (PR #543 리뷰 Blocker): received-first — 부모 mg 를 공유하는 중간 법인이
            // 하위 딜러 명목으로 부모 원장 풀을 얻지 않도록 자기 받은 레코드를 먼저 예산으로 채택한다.
            const ownReceived = ownReceivedBudget;
            const corpRecord = ownReceived ?? await queryCorpAllocation(packageId, resolvedMgrId, { product });
            if (!corpRecord) return { ok: false, reason: 'DEALER_ALLOCATION_NOT_FOUND' };
            if (!ownReceived) warnLedgerBudgetWithParent();
            // write 대상도 예산 레코드와 같아야 한다 — 부모 원장에 i 를 증분하면 부모 발행 이력이 오염된다.
            allocSk = ownReceived ? ownReceived.sk : skCorpAllocation(resolvedMgrId);
            const dealerList = await queryDealerAllocations(packageId, resolvedMgrId, { product });
            // owner corp 원장을 소비하므로 corp-direct path 와 동일하게 아래 concurrency
            // TODO(라인 960 부근, corpRev barrier 미도입)의 레이스에도 노출된다 — 완화는 defer.
            //
            // BUG-206 (#369): 종전 `computeCorpQuotaBreakdown().issuable`(t−i−Σd) 는 **발행 수(i)**
            // 기준이라 리딤(u)을 반영하지 못했다. i 는 리딤 전까지 무한 재발행되는 참고치여서
            // (quota-glossary §3) 소비와 무관하고, 외부 웹앱 경로로 리딤된 건은 i 를 올리지 않으므로
            // u>i 도 성립한다 → 같은 원장을 소비하는 corp-direct 분기와 천장이 갈렸다.
            // 가용량 SSoT(resolveSelfUsableCeiling: t−자기소모−Σ직속자식.t)로 통일한다.
            const ceiling = resolveSelfUsableCeiling(
                corpRecord,
                scopeLedgerChildren(dealerList, effectiveCorpId, corpRecord),
                effectiveCorpId,
                { allRows: dealerList },
            );
            currentIssued = corpRecord[issuedAttr] ?? 0;
            issuableByType = normalizedType === 'initial' ? ceiling.newQty : ceiling.renewQty;
            budgetOwnerCompanyId = effectiveCorpId;
        }
    } else {
        // BUG-206 R2 (PR #543 리뷰 Blocker): received-first — 자기 받은 레코드가 있으면 그것이 예산이고
        // write 대상이다. 원장 우선이면 mg 공유 중간 법인이 부모 원장을 자기 예산으로 쓴다.
        let corpRecord = ownReceivedBudget;
        allocSk = corpRecord ? corpRecord.sk : skCorpAllocation(resolvedMgrId);
        if (!corpRecord) {
            corpRecord = await queryCorpAllocation(packageId, resolvedMgrId, { product });
            if (corpRecord) warnLedgerBudgetWithParent();
        }
        if (!corpRecord) {
            // 중간 법인은 자기 corp 원장이 비어있고 예산이 상위에서 받은 레코드
            // (info:{상위mg}:{self})에 있음 — S5c AllocateToDealer 와 동일 폴백 패턴.
            // (자기 mg 는 해석됐지만 그 안에 원장도 받은 몫도 없는 하이브리드 — 위 exact 조회가 놓친다.)
            corpRecord = await queryDealerAllocationByDealerId(effectiveCorpId, { product });
            if (corpRecord) allocSk = corpRecord.sk;
        }
        if (!corpRecord) return { ok: false, reason: 'CORP_ALLOCATION_NOT_FOUND' };
        const dealerList = await queryDealerAllocations(packageId, resolvedMgrId, { product });
        // BUG-200: 발급 게이트 = 리딤 기준 selfUsable(t − Σ직계자식.t − 자기소모). corpRecord.u 는
        // 자식 리딤까지 합친 롤업이므로 자식 리딤분을 되빼야 "자기소모"만 남는다. 직계자식은
        // allocatedBy===자기 이고 셀프딜링(자기 자신) 레코드는 제외.
        //
        // BUG-206 (#369): 종전에는 롤업 보정을 **직속 자식 u 만** 되뺐다. 원장 u 는 mg 그룹 전체
        // 롤업이므로(prod 실데이터 확정 — quota-glossary §1.1) 손자가 리딤하면 그만큼 자기소모가
        // 과대 계산돼 천장이 과소해졌다(표시 노드모델과 불일치). 가용량 SSoT
        // resolveSelfUsableCeiling 은 보정 모집단을 mg 전체(ctx.allRows)로 잡아 이를 해소한다.
        // 3-seg 받은 레코드는 u 가 자기 리딤만 담으므로 보정하지 않는다(헬퍼가 sk 로 판별).
        const ceiling = resolveSelfUsableCeiling(
            corpRecord,
            scopeLedgerChildren(dealerList, effectiveCorpId, corpRecord),
            effectiveCorpId,
            { allRows: dealerList },
        );
        currentIssued = corpRecord[issuedAttr] ?? 0;
        issuableByType = normalizedType === 'initial' ? ceiling.newQty : ceiling.renewQty;
        budgetOwnerCompanyId = effectiveCorpId;
    }

    // BUG-196 — 발행 self-dealing 강제. 실제 소비되는 budget owner 가 actor 자신일 때만
    // 발행 허용(HQ su 전사 bypass). 기존 isAllocationActorInScope 는 clist 자손을 통과시켜
    // 상위 조직이 하위 조직에 할당해 준 예산을 대행 발행할 수 있었다(사용자 보고 갭). 발행은
    // 할당과 달리 자손 대행을 금지하므로 isIssuanceActorInScope(strict self)로 대체한다.
    // record 부재 케이스(위의 NOT_FOUND early-return)는 이미 반환되어 여기 도달하지 않는다.
    // 수량(issuableByType) 체크보다 앞서 실행해 스코프 밖 actor 에게 피해 노드의 잔여
    // quota 가 노출되지 않도록 한다(정보 노출 방지).
    if (!isIssuanceActorInScope(actorAuthUser, budgetOwnerCompanyId)) {
        console.warn('[guard] issuance budget owner not self (proxy issuance blocked)', {
            // 축 구분(SEC-055 리뷰): 이 가드는 **예산 주체**, 위 target-dealer-scope 는 **귀속 대상**.
            // 응답 사유는 정보 노출 차단 목적으로 둘 다 TARGET_OUT_OF_SCOPE 로 수렴시키므로,
            // 사고 분석은 이 필드로 구분한다.
            guard: 'budget-owner-self',
            handler: 'SmartDentAction.IssueQr',
            callerCompanyId: actorAuthUser?.companyId ?? null,
            targetCompanyId: budgetOwnerCompanyId,
            reason: 'issuance allowed only for own budget (self-dealing; HQ bypass)',
        });
        return { ok: false, reason: 'TARGET_OUT_OF_SCOPE' };
    }

    if (issuableByType < count) {
        return { ok: false, reason: 'INSUFFICIENT_QUOTA', issuable: issuableByType };
    }

    // 2. Atomically increment i_* with optimistic lock on the current value.
    //    u_* is never touched here — Homepage owns redeem accounting.
    // BUG-165: 부재 속성 ≠ 값 0 (동일 클래스). 딜러 i_* 는 create 분기가 항상 seed 해
    // dormant 하지만, corp 레코드는 FE 초기화 경로가 없어 최초 발행 시 i_* 가 부재일
    // 수 있다 — 스퓨리어스 QUOTA_UPDATE_CONFLICT 방지.
    const incrementResult = await DoDB('update', {
        TableName: TABLE_RAYTEAMS_LICENSE,
        Key: { _id: product.licenseAppId, sk: allocSk },
        UpdateExpression: 'ADD #i :count',
        ConditionExpression: lockEq('#i', ':expI', currentIssued),
        ExpressionAttributeNames: { '#i': issuedAttr },
        ExpressionAttributeValues: { ':count': count, ':expI': currentIssued },
        ReturnValues: 'ALL_NEW',
    });
    const updatedAllocation = normalizeUpdateResult(incrementResult);
    if (!updatedAllocation) {
        return { ok: false, reason: 'QUOTA_UPDATE_CONFLICT' };
    }

    // 3. Write QR records into rayteams-product-package. Each write is
    //    checked individually; on first failure we issue a compensating
    //    decrement for the unissued remainder and return a partial-failure
    //    envelope.
    // F-001(#561 PR 리뷰, no-silent-drop CLAUDE.md #7) — 원장 채택으로 QR write 에서 dealerId 가
    // 생략될 때 발급 요청당 1회 관측 warn 을 남긴다(개별 QR 레코드마다가 아니라 이 IssueQr
    // 호출 1회당 1회).
    if (omitDealerIdOnQr) {
        console.warn('[guard] IssueQr: QR dealerId omitted (ledger-budget self-issuance)', {
            handler: 'SmartDentAction.IssueQr',
            callerCompanyId: effectiveCorpId,
            managerGroupId: resolvedMgrId,
            count,
            reason: 'IMP-453 — 원장 예산 자기발급 QR 은 dealerId 대신 companyId 로 귀속, 리딤은 원장 u 로 정산',
        });
    }
    // F-002(#561 PR 리뷰) — 생략 발급(omitDealerIdOnQr)이면 QR 의 companyId 귀속을 caller 전달값이
    // 아니라 **검증된 예산 주체**(effectiveCorpId — corp-id-scope + budget-owner-self 가드를 이미
    // 통과한 값)로 강제한다. mg 공유 토폴로지(BUG-208)에서 receivedRecord 없는 두 회사가 각각
    // 원장 자기발급하면 dealerId 부재 QR 만으로는 주체를 구분할 수 없어(selfView 가 타사 QR 을
    // 반환, CWE-200) companyId 를 배타 경계로 세운다. 그 외(정상 dealerId 발급)는 기존 caller
    // 전달 companyId 를 그대로 쓴다.
    const qrCompanyId = omitDealerIdOnQr ? effectiveCorpId : companyId;
    const now = Date.now();
    const batchId = uuidv4();  // groups all QRs in this issuance for reprint (spec §5.5.1)
    const qrRecords = [];
    for (let i = 0; i < count; i += 1) {
        const token = generateQrToken();
        // sk 의 type 부분도 normalized 값('initial'/'renewal')으로 저장.
        const qrSk = `qr:${normalizedType}-${token}`;
        // managerGroupId 필드 = 실제 manager-group _id (UUID). 이전 historical 명명에서
        // corpId 가 들어가던 오류를 정정 (PR #91 review follow-up). queryIssuerRedeemedQrs
        // 호출자도 corpId 가 아닌 actual managerGroupId 를 전달하도록 함께 조정 필요.
        const record = {
            _id: packageId,
            sk: qrSk,
            // FEAT-023 Critical fix: QR payload 토큰을 별도 필드로 저장.
            // sk는 내부 정렬 키이므로 QR 값에 노출되면 안 됨(스펙 §5.5 + FEAT-022 PR #182 보안 리뷰).
            token,
            type: normalizedType,
            managerGroupId: resolvedMgrId,
            batchId,
            // IMP-453: omitDealerIdOnQr(원장 예산 채택)이면 write 시점에만 생략.
            ...(dealerId && !omitDealerIdOnQr ? { dealerId } : {}),
            // F-002: 생략 발급이면 qrCompanyId(=effectiveCorpId, 강제) 를 귀속 축으로 기록.
            ...(qrCompanyId ? { companyId: qrCompanyId } : {}),
            validuntil,
            used: 0,
            issuedAt: now,
        };
        const names = {
            '#type': 'type',
            '#used': 'used',
            '#sk': 'sk',
            // `token` 은 DDB reserved keyword 이므로 alias 필수
            '#token': 'token',
        };
        const values = {
            ':type': normalizedType,
            ':mgid': resolvedMgrId,
            ':valid': validuntil,
            ':used': 0,
            ':now': now,
            ':token': token,
            ':bid': batchId,
        };
        // token 과 batchId 도 DDB 에 저장 (이전에 누락되어 reprint 그룹핑 / QR payload 검증 불가).
        let setExpr =
            'SET #type = :type, managerGroupId = :mgid, validuntil = :valid, ' +
            '#used = :used, issuedAt = :now, #token = :token, batchId = :bid';
        if (dealerId && !omitDealerIdOnQr) {
            setExpr += ', dealerId = :did';
            values[':did'] = dealerId;
        }
        if (qrCompanyId) {
            setExpr += ', companyId = :cid';
            values[':cid'] = qrCompanyId;
        }
        const writeResult = await DoDB('update', {
            TableName: TABLE_RAYTEAMS_PRODUCT_PACKAGE,
            Key: { _id: packageId, sk: qrSk },
            UpdateExpression: setExpr,
            ConditionExpression: 'attribute_not_exists(#sk)',
            ExpressionAttributeNames: names,
            ExpressionAttributeValues: values,
        });
        if (isUpdateFailure(writeResult)) {
            const shortfall = count - i;
            await compensateQuotaDecrement(packageId, allocSk, issuedAttr, shortfall, product);
            return {
                ok: false,
                reason: 'QR_WRITE_PARTIAL',
                persisted: i,
                requested: count,
                qrRecords: qrRecords.slice(),
                compensatedBy: shortfall,
            };
        }
        qrRecords.push(record);
    }

    dispatch({ type: SmartDentConstant.ISSUE_QR, qrRecords, productKey: product.key });
    return { ok: true, qrRecords, updatedAllocation };
};

// ─── G group: Voucher template (FEAT-023, SPEC-RG-05) ──────────────────────

// PR #183 R6 리뷰 — companyId IDOR 방어: 호출자가 전달한 companyId 가 authed user
// 컨텍스트와 반드시 일치해야 쓰기를 허용. 서버 Lambda(DoDB put gateway) 에서
// JWT claims.companyId === _id 검증이 필수 전제이며, 클라이언트 이 가드는
// depth-in-defense 로 UI/콘솔 변조 경로를 차단.
const assertCallerOwnsCompanyId = (companyId, actorCompanyId) => {
    if (!companyId || typeof companyId !== 'string') {
        return 'INVALID_COMPANY_ID';
    }
    if (!actorCompanyId || typeof actorCompanyId !== 'string') {
        return 'MISSING_ACTOR_COMPANY_ID';
    }
    if (companyId !== actorCompanyId) {
        return 'COMPANY_ID_MISMATCH';
    }
    return null;
};

// PR #239 review (CQ Sug 1223 + Arch Minor 1369): selectActorOrg 셀렉터 헬퍼 추출.
// getState 가 함수가 아니거나 AuthReducer 가 없는 단위 테스트 환경도 안전하게 처리.
const selectActorOrg = (getState) => (
    typeof getState === 'function' ? getState()?.AuthReducer?.user?.companyInfo?.org ?? null : null
);

// [SMARTDent SPEC-RG-05] — 법인별 바우처 출력 템플릿 조회 (FEAT-028: MongoDB API).
//
// FE 는 항상 user 본인 companyId 로 호출한다. 상위 corp 템플릿이 필요한 경우(딜러가
// 자체 커스터마이즈 안 한 경우) backend cascade 가 자동으로 fallback 처리하여 응답에
// delegatedFrom 마커 포함하여 반환. 따라서 FE 가드는 본인 companyId 만 허용 strict 유지.
const GetVoucherTemplate = (companyId, options = {}) => async (dispatch, getState) => {
    const actorCompanyId = getState?.()?.AuthReducer?.user?.companyId ?? null;
    const mismatch = assertCallerOwnsCompanyId(companyId, actorCompanyId);
    if (mismatch) {
        console.warn('[SmartDentAction.GetVoucherTemplate]', mismatch);
        return null;
    }
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetVoucherTemplate', getState });
    if (!product) return null;
    try {
        const ret = await axios.get(
            `${AppConfig.API_SERVER_PATH}/voucher-template/${encodeURIComponent(companyId)}`,
            { ...JWT(), params: { product: product.key } },
        );
        let voucherTemplate = ret.data?.data ?? null;

        // IMP-191 (2026-05-12) + follow-up: 본인 템플릿 부재 시 직속 상위(법인→HQ,
        // 딜러→법인, HQ→HQ 본인) 의 manager group vouchers:default 로 fallback.
        // Confluence "Local License Check API doc" v22 "RAYGuard 에서 Voucher Template
        // 확인 방법". BE cascade 가 부족한 케이스 보강.
        // IMP-214: org 전달 — Branch/Subsidiary 분기 적용.
        // PR #239 review: selectActorOrg 헬퍼로 통일.
        const org = selectActorOrg(getState);
        const isEmptyTemplate = voucherTemplate === null
            || voucherTemplate === undefined
            || (typeof voucherTemplate === 'object' && Object.keys(voucherTemplate).length === 0);
        if (isEmptyTemplate) {
            try {
                voucherTemplate = await fetchDefaultVoucherTemplate(companyId, org, product);
            } catch (fallbackError) {
                console.error(
                    '[SmartDentAction.GetVoucherTemplate] fallback failed',
                    { message: fallbackError?.message, code: fallbackError?.code },
                );
                return null;
            }
        }

        dispatch({
            type: SmartDentConstant.GET_VOUCHER_TEMPLATE,
            voucherTemplate,
            productKey: product.key,
        });
        return voucherTemplate;
    } catch (error) {
        console.error('[SmartDentAction.GetVoucherTemplate]', { status: error?.response?.status, message: error?.message, code: error?.code, data: error?.response?.data });
        return null;
    }
};

// [SMARTDent SPEC-RG-05] — 법인별 커스텀 템플릿 저장 (FEAT-028: MongoDB API).
// qrBox 는 isValidQrBox 로 필수 검증 + clamp. 낙관적 잠금은 서버가 처리.
const SaveVoucherTemplate = (companyId, payload = {}, options = {}) => async (dispatch, getState) => {
    const actorCompanyId = getState?.()?.AuthReducer?.user?.companyId ?? null;
    const mismatch = assertCallerOwnsCompanyId(companyId, actorCompanyId);
    if (mismatch) {
        console.warn('[SmartDentAction.SaveVoucherTemplate]', mismatch);
        return { ok: false, reason: mismatch };
    }
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.SaveVoucherTemplate', getState });
    if (!product) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    const qrBox = payload.qrBox;
    if (!qrBox || typeof qrBox !== 'object') {
        return { ok: false, reason: 'INVALID_QR_BOX' };
    }
    const hasNaN = ['xPct', 'yPct', 'widthPct', 'heightPct']
        .some((k) => typeof qrBox[k] !== 'number' || !Number.isFinite(qrBox[k]));
    if (hasNaN) {
        return { ok: false, reason: 'INVALID_QR_BOX' };
    }
    const clamped = clampQrBox(qrBox);
    if (!isValidQrBox(clamped)) {
        return { ok: false, reason: 'INVALID_QR_BOX' };
    }
    // orientation — 'portrait' (default) | 'landscape'. 기타 값은 portrait 으로 정규화.
    const orientation = payload.orientation === 'landscape' ? 'landscape' : 'portrait';
    // bgBox — 이미지 위치/크기 박스(%). 기본 캔버스 전체. 0~100 범위 clamp.
    const rawBg = payload.bgBox;
    const bgBox = (rawBg && typeof rawBg === 'object') ? {
        xPct: Math.max(0, Math.min(100, Number(rawBg.xPct) || 0)),
        yPct: Math.max(0, Math.min(100, Number(rawBg.yPct) || 0)),
        widthPct: Math.max(1, Math.min(100, Number(rawBg.widthPct) || 100)),
        heightPct: Math.max(1, Math.min(100, Number(rawBg.heightPct) || 100)),
    } : { xPct: 0, yPct: 0, widthPct: 100, heightPct: 100 };
    // margins — 페이지 마진(%) {top, right, bottom, left}. 0~45 clamp + 합 90 제한.
    const rawM = payload.margins;
    const capM = (v) => Math.max(0, Math.min(45, Number(v) || 0));
    let mt = capM(rawM?.top);
    let mb = capM(rawM?.bottom);
    let ml = capM(rawM?.left);
    let mr = capM(rawM?.right);
    if (mt + mb > 90) { const k = 90 / (mt + mb); mt *= k; mb *= k; }
    if (ml + mr > 90) { const k = 90 / (ml + mr); ml *= k; mr *= k; }
    const margins = { top: mt, right: mr, bottom: mb, left: ml };
    // typeBox — 라이선스 type 라벨 위치/크기 박스(%). qrBox 와 동일 정규화.
    const rawType = payload.typeBox;
    const typeBox = (rawType && typeof rawType === 'object') ? {
        xPct: Math.max(0, Math.min(100, Number(rawType.xPct) || 0)),
        yPct: Math.max(0, Math.min(100, Number(rawType.yPct) || 0)),
        widthPct: Math.max(1, Math.min(100, Number(rawType.widthPct) || 25)),
        heightPct: Math.max(1, Math.min(100, Number(rawType.heightPct) || 4)),
    } : null;
    try {
        const ret = await axios.put(
            `${AppConfig.API_SERVER_PATH}/voucher-template/${encodeURIComponent(companyId)}`,
            {
                backgroundImageUrl: payload.backgroundImageUrl ?? null,
                imageChecksum: payload.imageChecksum ?? null,
                qrBox: clamped,
                orientation,
                bgBox,
                margins,
                ...(typeBox ? { typeBox } : {}),
                updater: payload.updater ?? null,
                prevUpdated: payload.prevUpdated ?? null,
            },
            { ...JWT(), params: { product: product.key } },
        );
        const saved = ret.data?.data ?? null;
        dispatch({
            type: SmartDentConstant.SAVE_VOUCHER_TEMPLATE,
            voucherTemplate: saved,
            productKey: product.key,
        });
        return { ok: true, record: saved };
    } catch (error) {
        console.error('[SmartDentAction.SaveVoucherTemplate]', { status: error?.response?.status, message: error?.message, code: error?.code });
        if (error?.response?.status === 409) {
            return { ok: false, reason: 'TEMPLATE_UPDATE_CONFLICT' };
        }
        return { ok: false, reason: 'GATEWAY_ERROR' };
    }
};

// [SMARTDent SPEC-RG-05] — 기본 템플릿으로 원복 (FEAT-028: MongoDB API).
// 서버가 isDefault=true, backgroundImageUrl=null, source='reset' 으로 저장.
// IMP-191 follow-up: Reset 후 rayteams-manager-group/vouchers:default 를 조회하여
// 표시 데이터(배경 이미지·qrBox) 를 채운다. 법인 계정의 경우 HQ manager group 의
// vouchers:default 를 가져와야 의미가 맞으므로 getParents() 로 HQ
// companyId 를 찾아 walk-up. HQ 본인 호출은 본인 managerGroupId 그대로 사용.
const ResetVoucherTemplateToHqDefault = (
    companyId,
    updater = null,
    options = {},
) => async (dispatch, getState) => {
    const actorCompanyId = getState?.()?.AuthReducer?.user?.companyId ?? null;
    const mismatch = assertCallerOwnsCompanyId(companyId, actorCompanyId);
    if (mismatch) {
        console.warn('[SmartDentAction.ResetVoucherTemplateToHqDefault]', mismatch);
        return { ok: false, reason: mismatch };
    }
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.ResetVoucherTemplateToHqDefault', getState });
    if (!product) return { ok: false, reason: 'INVALID_IDENTIFIER' };
    try {
        const ret = await axios.delete(
            `${AppConfig.API_SERVER_PATH}/voucher-template/${encodeURIComponent(companyId)}`,
            {
                ...JWT(),
                params: { product: product.key },
                data: {
                    updater: updater ?? null,
                    prevUpdated: options.prevUpdated ?? null,
                },
            },
        );
        const saved = ret.data?.data ?? null;

        // IMP-191 follow-up (2026-05-12): Reset 후 vouchers:default 를 조회하여 실제
        // 기본 템플릿(배경 이미지, qrBox 좌표) 으로 dispatch. 법인은 본인 manager group 에
        // vouchers:default 가 없으므로 getParents() 로 HQ companyId 를 찾아
        // HQ manager group 의 vouchers:default 를 가져온다. HQ 본인 호출은 본인 그대로.
        // 낙관적 잠금 정합성 — voucherTemplate.updated 는 BE doc(saved) 의 updated 를
        // 보존해야 두번째 Reset/Save 의 prevUpdated 검증이 통과한다 (fallback 데이터의
        // updated 를 그대로 두면 stale → 409).
        // IMP-214: getState 에서 org 를 읽어 Branch/Subsidiary 분기 전달.
        // PR #239 review: selectActorOrg 헬퍼로 통일.
        const org = selectActorOrg(getState);
        let fallbackRecord = null;
        try {
            fallbackRecord = await fetchDefaultVoucherTemplate(companyId, org, product);
        } catch (fallbackError) {
            console.error(
                '[SmartDentAction.ResetVoucherTemplateToHqDefault] fallback failed',
                { message: fallbackError?.message, code: fallbackError?.code },
            );
            // fallback 실패 시 BE reset 응답(saved) 만으로 dispatch.
        }

        // M4 (낙관적 잠금 안전화): saved.updated 가 없으면 dispatch skip.
        // updated 가 null 이면 후속 Save/Reset 의 prevUpdated=null 로 전달되어
        // BE 낙관적 잠금 우회(항상 신규 취급) 또는 항상-409 회귀 위험 존재.
        // 안전 정책: updated 부재 시 { ok: false, reason: 'RESET_NO_UPDATED_FIELD' }.
        if (saved?.updated == null) {
            console.warn(
                '[SmartDentAction.ResetVoucherTemplateToHqDefault] saved.updated 없음 → dispatch skip',
                String(saved).slice(0, 100),
            );
            return { ok: false, reason: 'RESET_NO_UPDATED_FIELD' };
        }

        const effectiveTemplate = fallbackRecord
            ? {
                ...fallbackRecord,
                // 낙관적 잠금 정합성 — BE doc 메타데이터 보존.
                companyId: saved?.companyId ?? fallbackRecord.companyId ?? companyId,
                updated: saved.updated,
            }
            : saved;

        dispatch({
            type: SmartDentConstant.RESET_VOUCHER_TEMPLATE,
            voucherTemplate: effectiveTemplate,
            productKey: product.key,
        });
        return { ok: true, record: effectiveTemplate };
    } catch (error) {
        console.error('[SmartDentAction.ResetVoucherTemplateToHqDefault]', { status: error?.response?.status, message: error?.message, code: error?.code });
        if (error?.response?.status === 409) {
            return { ok: false, reason: 'TEMPLATE_UPDATE_CONFLICT' };
        }
        return { ok: false, reason: 'GATEWAY_ERROR' };
    }
};

// ─── H group: Allocation history (FEAT-024 SPEC-RG-01 AC-3 / SPEC-RG-02 AC-7) ──

// 법인이 본사로부터 부여받은 이력(grant) 조회.
// audit 레코드 중 operation = 'grant' 만 필터링. HQ 측 부여 시점에서도 동일
// 스키마로 audit 가 기록된다는 전제 (Backend HQ admin 도구가 동일 putAllocationAudit
// 패턴 사용). 부재 시 빈 배열 반환.
//
// PR #184 R1 FYI #1 — FE-side IDOR 가드 (defense-in-depth).
// 호출자가 임의 corpId 로 audit 를 읽어가는 것을 방지하기 위해 Redux Auth user.companyId
// 와 일치 여부를 강제. Backend Lambda 가 권한 경계지만, 동일 파일의 write 경로
// (assertCallerOwnsCompanyId) 와 일관성을 맞춰 read 도 차단한다. UNAUTHENTICATED /
// COMPANY_ID_MISMATCH 시 빈 배열 + dispatch 안 함.
const GetGrantHistory = (corpId, options = {}) => async (dispatch, getState) => {
    if (!corpId || typeof corpId !== 'string') {
        return [];
    }
    const authUser = getState?.()?.AuthReducer?.user;
    if (!authUser?.companyId) {
        console.warn('[SmartDentAction.GetGrantHistory] UNAUTHENTICATED');
        return [];
    }
    if (authUser.companyId !== corpId) {
        console.warn('[SmartDentAction.GetGrantHistory] COMPANY_ID_MISMATCH');
        return [];
    }
    // PR #191 review (Security Minor) — 진단 console.log 제거. raw audit 페이로드(corpId,
    // delta_initial 등)가 production 콘솔에 노출되어 PII 누출 위험. 에러 경로 console.error
    // 만 유지.
    try {
        const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetGrantHistory', getState });
        if (!product) return [];
        const audits = await queryAllocationAudits(corpId, {
            ...options,
            operation: 'grant',
            product,
        });
        // queryAllocationAudits 가 FilterExpression 으로 server-side 필터링.
        // 안전망 — DDB FilterExpression 미적용 환경 (구버전 게이트웨이 등) 대비 client-side 재필터.
        const grantOnly = audits.filter((a) => a?.operation === 'grant');
        dispatch({
            type: SmartDentConstant.GET_GRANT_HISTORY,
            grantHistory: grantOnly,
            productKey: product.key,
        });
        return grantOnly;
    } catch (error) {
        console.error('[SmartDentAction.GetGrantHistory] ERROR', error);
        return [];
    }
};

// 법인의 딜러 할당/회수 이력 조회. dealerId 미전달 시 전체, 전달 시 해당 딜러로 필터.
// audit 레코드 중 operation ∈ {'allocate', 'revoke'} 만 반환.
//
// PR #184 R1 FYI #1 — IDOR 가드 (위 GetGrantHistory 와 동일 패턴).
const GetDealerAllocationHistory = (
    corpId,
    dealerId = null,
    options = {},
) => async (dispatch, getState) => {
    if (!corpId || typeof corpId !== 'string') {
        return [];
    }
    const authUser = getState?.()?.AuthReducer?.user;
    if (!authUser?.companyId) {
        console.warn('[SmartDentAction.GetDealerAllocationHistory] UNAUTHENTICATED');
        return [];
    }
    if (authUser.companyId !== corpId) {
        console.warn('[SmartDentAction.GetDealerAllocationHistory] COMPANY_ID_MISMATCH');
        return [];
    }
    // PR #191 review (Security Minor) — 진단 console.log 제거 (PII 노출 차단).
    try {
        const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetDealerAllocationHistory', getState });
        if (!product) return [];
        // PR #184 R1 FYI #3 — DDB-level 필터로 client-side limit + filter 함정 해소.
        // operation 옵션 미전달 시 query 가 allocate/revoke 동시 반환 (FilterExpression 'IN').
        const audits = await queryAllocationAudits(corpId, {
            ...options,
            operationsIn: ['allocate', 'revoke'],
            product,
        });
        const filtered = audits.filter((a) => {
            if (a?.operation !== 'allocate' && a?.operation !== 'revoke') return false;
            if (dealerId && a?.target !== dealerId) return false;
            return true;
        });
        dispatch({
            type: SmartDentConstant.GET_DEALER_ALLOCATION_HISTORY,
            dealerAllocationHistory: filtered,
            productKey: product.key,
        });
        return filtered;
    } catch (error) {
        console.error('[SmartDentAction.GetDealerAllocationHistory] ERROR', error);
        return [];
    }
};

// ─── J group: FEAT-050 S5a — N단계 서브트리 할당 노드 fan-out 조회 ───────────────────

// [FEAT-050] N단계 딜러 계층(법인 → 딜러 → 손자딜러 …)의 할당 노드를 BFS 로 전부
// 수집한다. 기존 GetCorpAllocation/GetDealerAllocations 는 1단계(법인+직속 딜러)만
// 조회했으나, 러시아 등 N단계 조직은 손자 이하 계층의 할당 현황도 필요.
//
// 각 노드는 companyId(자기 자신) + allocatedBy(직속 상위 companyId) 를 태깅해
// buildNodesByCompanyId 로 { [companyId]: record } 맵을 조립한다. 루트는
// queryCorpAllocation(법인 자기 예산, allocatedBy=null), 그 외는 상위가 조회한
// queryDealerAllocations 항목에서 sk 로 자기 companyId 를 역산한다.
//
// 순환 방어: visited Set 으로 이미 처리한 companyId 는 재조회(GetManagerGroupIdByCompanyId
// 재호출)하지 않고, 이미 방문한 노드를 가리키는 역참조 레코드도 태깅하지 않는다
// (조상을 가리키는 손상된/순환 데이터가 있어도 루트 노드를 덮어쓰지 않음).
// FEAT-050 S5b (#277): 호출자-스코프 인가 가드. rootCompanyId 가 caller 소유(자기
// companyId 또는 clist 서브트리) 밖이면 조회 없이 빈 맵을 반환한다 (read-scope IDOR
// 방지). 인가식(HQ su bypass + clist 스코프)은 isAllocationActorInScope SSoT 재사용.
// BUG-211: options.fresh — root 반복의 queryCorpAllocation/queryDealerAllocations 가
// GetCorpAllocation/GetDealerAllocations 와 같은 managerGroupId 로 완전히 동일한 응답을
// 각자 조회하던 중복을, 표시 경로는 dedupe(3초 TTL)로 합치고 write-직후 refresh(onSuccess)
// 는 fresh:true 로 우회해 방금 쓴 값이 즉시 보이게 한다. fresh 는 **root 반복에만** 적용된다
// (R2 리뷰) — BFS 전체 노드에 걸면 무관한 형제/자손까지 이 캐시는 물론 scopeContext.js 의
// 3초 캐시·managerGroupResolver.js 의 5분 구조 캐시까지 우회해, 서브트리가 클수록 쿼리가
// 오히려 이 PR 이전보다 늘어난다.
const GetAllocationSubtree = (packageId, rootCompanyId, options = {}) => async (dispatch, getState) => {
    if (!rootCompanyId) return {};
    // 범위 가드의 슬롯 비우기 dispatch 가 제품 키를 써야 하므로 제품 해석을 가드 앞에 둔다.
    // 목록 밖 제품은 다른 제품 슬롯을 건드리지 않도록 dispatch 없이 반환(warn 은 resolveLicenseProduct 가 남김).
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.GetAllocationSubtree', getState });
    if (!product) return {};
    const actor = getState?.()?.AuthReducer?.user ?? null;
    if (!isAllocationActorInScope(actor, rootCompanyId)) {
        console.warn('[guard] GetAllocationSubtree root out of scope', {
            handler: 'SmartDentAction.GetAllocationSubtree',
            callerCompanyId: actor?.companyId ?? null,
            rootCompanyId,
            reason: 'root company not in caller scope (self or clist)',
        });
        // IMP-420 M6: 슬롯을 비워야 useNodeModel=false 로 legacy 폴백 — dispatch 누락 시
        // 직전 노드가 스테일로 남는다(fail-closed).
        dispatch({ type: SmartDentConstant.GET_ALLOCATION_NODES, allocationNodes: {}, productKey: product.key });
        return {};
    }
    try {
        const tagged = [];
        const visited = new Set();
        // IMP-426: 공유 mg 조직(중간법인들이 같은 부모 mg 를 쓰는 구조)에서 노드마다
        // 동일 managerGroupId 를 재조회하던 것을 BFS 1회 실행 범위로 캐시해 제거.
        // 키가 managerGroupId 뿐이고 packageId 를 포함하지 않음 — 이 BFS 실행 동안은
        // packageId 가 함수 인자로 고정이라 안전(한 번의 GetAllocationSubtree 호출 범위를
        // 벗어나 재사용되지 않는 로컬 Map).
        const dealersByMg = new Map();
        const queue = [rootCompanyId];
        let isRoot = true;
        while (queue.length > 0) {
            const cid = queue.shift();
            if (!cid || visited.has(cid)) continue;
            visited.add(cid);

            const wasRoot = isRoot;
            isRoot = false;
            // R2 리뷰(Major): fresh 는 write 가 실제로 일어난 root 에만 적용한다. cid 전체에
            // 걸면 서브트리의 무관한 형제/자손 노드까지 이 PR 의 dedupe 캐시는 물론
            // scopeContext.js 의 3초 캐시·managerGroupResolver.js 의 5분 구조 캐시까지 함께
            // 우회해, Allocate/Revoke 1회로 서브트리 크기에 비례해 쿼리가 오히려 늘어난다.
            const nodeFresh = wasRoot && !!options.fresh;

            const { managerGroupId, isSubEntity, receivedRecord } = await resolveScopeContext(
                cid, { ...(nodeFresh ? { fresh: true } : {}), product },
            );
            if (wasRoot && isSubEntity && receivedRecord) {
                // 루트가 중간법인(공유/부모 mg 또는 mg-null)이면 받은 레코드가 자기 corp 예산.
                tagged.push({ ...receivedRecord, companyId: cid, allocatedBy: null });
            }
            if (!managerGroupId) continue; // 여전히 없으면(받은 레코드 없는 진짜 leaf) 스킵 — 부모가 이미 태깅.

            if (wasRoot && !isSubEntity) {
                const corp = await queryCorpAllocation(packageId, managerGroupId, { dedupe: true, fresh: nodeFresh, product });
                if (corp) tagged.push({ ...corp, companyId: cid, allocatedBy: null });
            }

            // IMP-426: 동일 managerGroupId 를 여러 cid 가 공유하는 구조에서 BFS 1회 실행 범위로
            // 원시 조회를 캐시(dealersByMg) — scopeOwnDealers 는 순수 filter 라 캐시된 원본을
            // 노드마다 다시 필터링해도 오염되지 않는다. PR #539 리뷰 Suggestion: 캐시 히트 판정을
            // 값(undefined)이 아니라 키 존재 여부로 — queryDealerAllocations 가 undefined 를
            // 반환해도 dedup 이 무효화되지 않음.
            if (!dealersByMg.has(managerGroupId)) {
                dealersByMg.set(
                    managerGroupId,
                    await queryDealerAllocations(packageId, managerGroupId, { dedupe: true, fresh: nodeFresh, product }),
                );
            }
            let dealers = dealersByMg.get(managerGroupId);
            // #498 Major1: 공유mg self-resolve 노드도 own-scope.
            // IMP-420(#354) 문제 2 — 노드모델 잔여(브라우저 실측 발견): 종전 `if (isSubEntity)` 조건
            // 때문에 최상위 순회에서는 필터가 빠져, 같은 mg 네임스페이스의 **손자**까지 자기 직속
            // 자식으로 태깅됐다(아래 `allocatedBy: cid` 덮어쓰기로 원본 귀속도 사라져 buildNodesByCompanyId
            // 의 sk-dedup 이 나중의 올바른 태깅을 드롭). 그 결과 getDirectAllocationChildren 이 손자를
            // 직속으로 세어 selfUsable 이 과소 계산됐다(실측: 10−1−(5+2)=2, 정답 10−1−5=4).
            // tier 무관 직속 필터로 통일 — 손자는 자기 부모 순회에서 올바른 allocatedBy 로 태깅된다.
            dealers = scopeOwnDealers(dealers, cid, isSubEntity);
            // PR #538 리뷰 Major #2(보안) 검토 결과 — **미적용**: "부재(allocatedBy 없음) 행을 노드맵
            // 태깅에서 제외" 제안은 기존 계약과 충돌해 채택하지 않는다. `allocatedBy` 부재 행은 이
            // 저장소에서 **정상 직속 자식(레거시 레코드)** 으로 지원 중이며, 그 계약을 명시적으로
            // 고정한 테스트가 있다(SmartDentAction.fetchAllocationSubtree.test.js 3건 —
            // "다단계 BFS 수집"·"allocatedBy 를 상위 노드 companyId 기준으로 태깅"·"순환 참조",
            // bug184.mgNullReadHardening.test.js 1건). 제외하면 이 정상 케이스가 노드맵에서 사라져
            // 잔여가 과대 표시된다. 반면 리뷰가 우려한 "부재 행이 실제로는 손자" 케이스는 실측 prod
            // 데이터에 존재하지 않는다(3-seg 행 전부 allocatedBy 보유 — 2026-07-31 브라우저 확인).
            // 부재 행만으로는 "정상 직속"과 "귀속불명 손자"를 구조적으로 구분할 수 없어, 판별 근거
            // (companies 트리 대조 등)를 도입하는 별도 이슈로 분리한다. 여기서는 종전 계약을 유지한다.
            (Array.isArray(dealers) ? dealers : []).forEach((d) => {
                const childId = extractDealerIdFromSkStrict(d?.sk); // 신뢰 경계(맵 키·큐 시드): strict(3-seg 검증, fallback 우회 차단)
                if (!childId) return;
                const isSelfDeal = childId === cid; // IMP-420: 자기참조도 태깅 대상(흡수 성립 위해 allocatedBy=cid 유지), 단 재큐잉은 금지(무한루프 방지)
                if (isSelfDeal || !visited.has(childId)) {
                    tagged.push({ ...d, companyId: childId, allocatedBy: cid });
                }
                // N5: self-deal 재큐잉은 cid 가 이미 visited 라 visited 검사만으로 차단
                // — isSelfDeal 은 태깅 조건에만 load-bearing.
                if (!visited.has(childId)) {
                    queue.push(childId);
                }
            });
        }

        const allocationNodes = buildNodesByCompanyId(tagged);
        dispatch({ type: SmartDentConstant.GET_ALLOCATION_NODES, allocationNodes, productKey: product.key });
        return allocationNodes;
    } catch (error) {
        console.error('[SmartDentAction.GetAllocationSubtree]', error);
        // N3: catch 는 슬롯 미변경(늦은 실패가 최신 성공 덮는 레이스 방지). 스테일 완전
        // 해소는 slot rootCompanyId 키잉 후속(#354). 스코프 가드는 결정적 no-access라 빈 맵 유지.
        console.warn(
            '[SmartDentAction.GetAllocationSubtree] fetch failed — falling back to legacy 2-tier (slot unchanged)',
            { rootCompanyId, reason: error?.message }
        );
        return {};
    }
};

// ─── BUG-237: 자기발급 QR 리딤 시 voucher_license 무-귀속 행 소급 백필 ───────────
//
// 배경: IMP-453 이후 최상위 법인이 원장(2-seg) 예산으로 자기발급하면 QR 에 dealerId 를
// 기록하지 않는다(IssueQr). 리딤(rayteams 측)이 POST /voucher-license 로 저장할 때
// companyId 를 함께 보내지 않아 voucher_license 행이 dealerId·companyId 둘 다 없는
// 무-귀속 상태로 남는다 — Customer Licenses 조회 스코프(dealerId ∈ [딜러] OR
// (dealerId 없음 AND companyId ∈ [딜러]))의 어느 절에도 걸리지 않아 목록이 비고,
// DealerIssuePanel 의 canOperate && dealerCustomerLicenses.length > 0 게이트 때문에
// PC Seat Activation 패널도 숨겨진다. BE 는 이미 backfill 을 지원한다(BUG-232) — 같은
// sk 로 귀속 축을 실어 POST 하면 409 대신 채워 200 {ok:true, sk, backfilled:true} 를
// 준다(BE 변경 없음). 이 thunk 는 rayteams DDB QR 스냅샷(companyId/used/licenses[].lcsk)
// 을 근거로 누락된 POST 를 재발행한다.
//
// 자기발급(dealerId === corpId, 원장 소유자) 한정 — 중간법인(받은-레코드 존재)은
// 원장이 자기 예산이 아니므로 대상 아님(resolveScopeContext.receivedRecord 로 판정).
//
// F-003(#575 PR 리뷰): 반환 `conclusive` — "이번 실행이 결론에 도달했는가". 호출부
// (DealerIssuePanel) 는 conclusive===true 일 때만 탭 세션 마커를 찍는다(재시도 여지 보존).
//   - true:  스코프 판정 정상 + QR 조회가 실제 데이터를 돌려줬고, 후보가 없거나 모든 후보
//            처리가 끝난 경우. not-self-issuance/not-ledger-owner(대상 아님)도 결론이다.
//   - false: no-manager-group(일시 장애와 구분 불가) · QR 조회가 전 패키지에서 0건(DoDB
//            가 실패를 [] 로 삼켜 진짜 0건과 구분 불가) · 기존목록 GET 실패 · meta/POST 가
//            후보 전건 실패.
// 게이트웨이가 LastEvaluatedKey 를 내부 소진해 전량 반환하므로 조회 잘림은 발생하지
// 않는다(2026-09-09 실측: Limit=1/500/100000 모두 동일 건수). 따라서 잘림 감지 축은
// 두지 않는다.
const BackfillUnattributedVoucherLicenses = (dealerId, options = {}) => async (_dispatch, getState) => {
    const { corpId } = options;
    const product = resolveLicenseProduct(options?.product, { handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses', getState });
    if (!product || !dealerId || !corpId) {
        // invalid-args 는 인자 문제 — 재시도해도 결과가 같다(결론). 목록 밖 제품도 같은 실패로 수렴.
        return { ok: false, backfilled: 0, skipped: 'invalid-args', conclusive: true };
    }
    if (dealerId !== corpId) {
        // 정상 분기(자기발급 대상 아님) — CLAUDE.md §7 silent-drop 예외: 정상 흐름이라 warn 불필요.
        return { ok: false, backfilled: 0, skipped: 'not-self-issuance', conclusive: true };
    }
    try {
        const { managerGroupId, receivedRecord } = await resolveScopeContext(corpId, { product });
        if (!managerGroupId) {
            console.warn('[SmartDentAction.BackfillUnattributedVoucherLicenses] skip', {
                handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses',
                dealerId,
                reason: 'no manager group resolved for corpId',
            });
            // F-003: 게이트웨이 read 일시 실패로도 mg 가 비어 도출될 수 있어 미결론 — 마킹 보류.
            return { ok: false, backfilled: 0, skipped: 'no-manager-group', conclusive: false };
        }
        if (receivedRecord) {
            console.warn('[SmartDentAction.BackfillUnattributedVoucherLicenses] skip', {
                handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses',
                dealerId,
                reason: 'corp holds a received record — mid-tier, not ledger owner',
            });
            return { ok: false, backfilled: 0, skipped: 'not-ledger-owner', conclusive: true };
        }

        const allQrs = [];
        for (const packageId of product.packageIds) {
            const qrs = await queryIssuedQrsByDealer(packageId, dealerId, {
                includeLedgerIssued: true,
                managerGroupId,
            });
            const list = Array.isArray(qrs) ? qrs : [];
            allQrs.push(...list);
        }

        if (allQrs.length === 0) {
            // F-002/F-003: DoDB 는 read 실패를 [] 로 삼켜(src/lib/ddb.js:195) "진짜 0건"과
            // "조회 실패"를 구분할 수 없다 — 전 패키지 0건은 보수적으로 미결론 처리.
            console.warn('[SmartDentAction.BackfillUnattributedVoucherLicenses] QR query returned 0 across all packages — cannot distinguish empty result from a swallowed read failure', {
                handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses',
                dealerId,
                reason: 'zero-results-ambiguous',
            });
            return { ok: true, backfilled: 0, conclusive: false };
        }

        const targets = allQrs.filter((qr) => qr && qr.used > 0 && !qr.dealerId && qr.companyId === dealerId);
        if (targets.length === 0) {
            return { ok: true, backfilled: 0, conclusive: true };
        }

        // 기존 저장분 sk 집합 — 이미 존재하는 행은 재발행 대상에서 제외(중복 POST 방지).
        let existingSks = new Set();
        let existingListFetchFailed = false;
        try {
            const res = await axios.get(
                `${AppConfig.API_SERVER_PATH}/voucher-license`,
                { ...JWT(), params: { status: 'all', dealerId, product: product.key } },
            );
            const items = res?.data?.data?.items ?? [];
            existingSks = new Set((Array.isArray(items) ? items : []).map((item) => item?.sk).filter(Boolean));
        } catch (error) {
            existingListFetchFailed = true;
            console.warn('[SmartDentAction.BackfillUnattributedVoucherLicenses] existing list fetch failed — proceeding without exclusion', {
                handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses',
                dealerId,
                reason: error?.message ?? 'unknown error',
            });
        }

        // 후보 lcsk 수집 — 문자열이고 lcuse: 접두 + 전역 중복 제거 + 기존 저장분 제외.
        const seenLcsk = new Set();
        const candidates = [];
        for (const qr of targets) {
            const licenses = Array.isArray(qr.licenses) ? qr.licenses : [];
            for (const lic of licenses) {
                const sk = lic?.lcsk;
                if (typeof sk !== 'string' || !sk.startsWith('lcuse:')) continue;
                if (seenLcsk.has(sk) || existingSks.has(sk)) continue;
                seenLcsk.add(sk);
                candidates.push({ qr, lic, sk });
            }
        }

        let backfilled = 0;
        let metaAttempted = 0;
        let metaFailed = 0;
        let postAttempted = 0;
        let postFailed = 0;
        for (const { qr, lic, sk } of candidates) {
            // lcuse:{country}:{groupId}:{userId}:{type}:{licenseId} — 3번째 세그먼트가 groupId.
            // 기형 sk 는 meta 조회가 어차피 빗나가므로, 호출 자체를 아끼고 명시적으로 건너뛴다.
            // (재시도해도 sk 는 동일하게 기형이므로 결론성 판정에는 영향 없음 — meta/POST 시도
            // 자체가 없었던 후보다.)
            const groupId = sk.split(':')[2];
            if (!groupId) {
                console.warn('[SmartDentAction.BackfillUnattributedVoucherLicenses] malformed lcsk — skip candidate', {
                    handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses',
                    dealerId,
                    sk,
                    reason: 'groupId segment missing in lcuse sk',
                });
                continue;
            }
            let metaOk = false;
            metaAttempted += 1;
            try {
                const metaRes = await axios.get(
                    `${AppConfig.API_SERVER_PATH}/voucher-license/meta`,
                    { ...JWT(), params: { userId: qr.userid, groupId, product: product.key } },
                );
                metaOk = metaRes?.status === 200;
            } catch (error) {
                metaFailed += 1;
                console.warn('[SmartDentAction.BackfillUnattributedVoucherLicenses] meta check failed — skip candidate (avoid creating a record with missing email/phoneNumber/groupName)', {
                    handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses',
                    dealerId,
                    sk,
                    reason: error?.message ?? 'unknown error',
                });
                continue;
            }
            if (!metaOk) continue;

            const body = {
                sk,
                expired: lic.expired,
                companyId: qr.companyId,
                token: qr.token,
                managerGroupId: qr.managerGroupId,
                userId: qr.userid,
                groupId,
                type: qr.type,
                product: product.key,
            };
            postAttempted += 1;
            try {
                await axios.post(`${AppConfig.API_SERVER_PATH}/voucher-license`, body, JWT());
                backfilled += 1;
            } catch (error) {
                postFailed += 1;
                console.warn('[SmartDentAction.BackfillUnattributedVoucherLicenses] backfill POST failed — continuing with remaining candidates', {
                    handler: 'SmartDentAction.BackfillUnattributedVoucherLicenses',
                    dealerId,
                    sk,
                    reason: error?.message ?? 'unknown error',
                });
            }
        }

        // F-003: meta/POST 후보 전건 실패는 "이번 실행이 결론에 도달하지 못함" — 재시도해야
        // 남은 후보가 처리된다. 일부만 실패하면 다음 세션에서 잔여 후보(existingSks 미포함
        // 이라 계속 후보로 남는다)로 재수렴하므로 결론으로 취급한다.
        const metaAllFailed = metaAttempted > 0 && metaFailed === metaAttempted;
        const postAllFailed = postAttempted > 0 && postFailed === postAttempted;
        const conclusive = !existingListFetchFailed && !metaAllFailed && !postAllFailed;

        return { ok: true, backfilled, conclusive };
    } catch (error) {
        console.error('[SmartDentAction.BackfillUnattributedVoucherLicenses]', error?.message ?? error);
        // 예상치 못한 예외 — 결론에 도달하지 못한 실행으로 취급(마킹 보류, 다음 진입 재시도).
        return { ok: false, backfilled: 0, error: true, conclusive: false };
    }
};

// ─── FEAT-033: PC Seat Activation — PcActivationAction.js 로 이동 (IMP-151 AC-1) ───────────

export const SmartDentAction = {
    GetPackages,
    GetPackage,
    GetCorpAllocation,
    GetDealerAllocations,
    GetDealerAllocation,
    GetMyDealerAllocation,
    QueryMyReceivedAllocation,
    AllocateToDealer,
    RevokeFromDealer,
    IssueQr,
    GetCustomerLicenses,
    GetDealerCustomerLicenses,
    GetVoucherMetaByUser,
    GetIssuedQrsByDealer,
    QueryIssuedQrsForDealers,
    QuerySubOrgCustomerLicenses,
    SuspendLicense,
    ResumeLicense,
    UpdateLicenseMemo,
    GetVoucherTemplate,
    SaveVoucherTemplate,
    ResetVoucherTemplateToHqDefault,
    GetGrantHistory,
    GetDealerAllocationHistory,
    GetAllocationSubtree,
    BackfillUnattributedVoucherLicenses,
};
